import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Search, Upload, FileText, Download, Home, RefreshCw, CheckCircle, AlertCircle, 
  File, User, Edit, Trash, Plus, X, Building, Copy, Star, ChevronDown, ChevronUp, 
  FileSpreadsheet, Brain, Sparkles, Zap, Users, BarChart, HelpCircle, Loader,
  Info, Shield, ArrowUpRight, Maximize, Server, LayoutGrid, Settings, AlignJustify
} from 'lucide-react';
import localforage from 'localforage';

/**
 * Premium Room Management Component
 * Highly polished with premium animations, cutting-edge design, and AI features
 */
const PremiumRoomManagement = ({ users = [], fetchUsers }) => {
  // Core data state
  const [roomAssignments, setRoomAssignments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [searchText, setSearchText] = useState('');
  
  // File handling state
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [processingStatus, setProcessingStatus] = useState(null);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [useSmartProcessing, setUseSmartProcessing] = useState(true);
  const [confidenceScore, setConfidenceScore] = useState(0);
  const [showAiInfo, setShowAiInfo] = useState(false);
  
  // UI state
  const [activeTab, setActiveTab] = useState('upload');
  const [showManualForm, setShowManualForm] = useState(false);
  const [showExportPanel, setShowExportPanel] = useState(false);
  const [showCSVFormat, setShowCSVFormat] = useState(false);
  const [exportFormat, setExportFormat] = useState('csv');
  const [copySuccess, setCopySuccess] = useState('');
  const [formMode, setFormMode] = useState('add'); // 'add' or 'edit'
  const [isAiAnalyzing, setIsAiAnalyzing] = useState(false);
  const [showAiSuggestions, setShowAiSuggestions] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'table'
  const [animatedCount, setAnimatedCount] = useState(false);
  
  // Stats
  const [stats, setStats] = useState({
    total: 0,
    assigned: 0,
    unassigned: 0
  });
  
  // Form state
  const [manualFormData, setManualFormData] = useState({
    userId: null,
    email: '',
    roomNumber: '',
    building: ''
  });
  const [formErrors, setFormErrors] = useState({});
  
  // Animation refs
  const exportTextRef = useRef(null);
  const headerRef = useRef(null);
  const statsRef = useRef([]);
  const suggestionRefs = useRef([]);
  const fileDropRef = useRef(null);
  
  // Constants
  const BASE_URL = 'https://lager-app-api.sunrise-avengers.ch:443';
  const preferredUsers = ['neo.stutz@sunrise.net', 'roman.stammbacj@sunrise.net'];
  const aiModelVersion = "0.5";
  const aiUpdateDate = "15.03.2025";

  // Premium colors
  const COLORS = {
    light: {
      primary: '#da291c', // Indigo
      primaryDark: '#8d989b',
      primaryLight: '#a5b4fc',
      secondary: '#ef7c3c', // Pink
      secondaryDark: '#db2777',
      accent: '#99b2a7', // Cyan
      success: '#10b981',
      warning: '#f59e0b',
      error: '#ef4444',
      surface: '#ffffff',
      surfaceAlt: '#f8fafc',
      surfaceHover: '#f1f5f9',
      border: '#e2e8f0',
      borderAlt: '#cbd5e1',
      text: '#0f172a',
      textMedium: '#334155',
      textLight: '#64748b'
    },
    dark: {
      primary: '#e2271e', // Indigo
      primaryDark: '#000',
      primaryLight: '#c7d2fe',
      secondary: '#f472b6', // Pink
      secondaryDark: '#ec4899',
      accent: '#22d3ee', // Cyan
      success: '#34d399',
      warning: '#fbbf24',
      error: '#f87171',
      surface: '#000',
      surfaceAlt: '#1e293b',
      surfaceHover: '#334155',
      border: '#334155',
      borderAlt: '#475569',
      text: '#f8fafc',
      textMedium: '#cbd5e1',
      textLight: '#94a3b8'
    }
  };

  // Fetch room assignments
  const fetchRoomAssignments = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const token = await localforage.getItem('token');
      if (!token) throw new Error('Kein Token gefunden');

      const response = await fetch(`${BASE_URL}/api/rooms/admin-assignments`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Fehler beim Laden der Zimmerzuweisungen');

      const data = await response.json();
      
      // Sort by room number
      const sortedAssignments = data.roomAssignments?.sort((a, b) => {
        return a.roomNumber.localeCompare(b.roomNumber, undefined, { numeric: true });
      }) || [];
      
      setRoomAssignments(sortedAssignments);
      
      // Calculate stats
      const allUsersCount = Array.isArray(users) ? users.length : 0;
      let assignedCount = sortedAssignments.length;
      
      setStats({
        total: allUsersCount,
        assigned: assignedCount,
        unassigned: allUsersCount - assignedCount
      });
      
      // Trigger count animation
      setAnimatedCount(true);
      setTimeout(() => {
        setAnimatedCount(false);
      }, 2000);
      
    } catch (err) {
      console.error('Error loading room assignments:', err);
      setError('Fehler beim Laden der Zimmerzuweisungen: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  }, [BASE_URL, users]);

  // Initial data load
  useEffect(() => {
    if ((Array.isArray(users) && users.length > 0) || !users) {
      fetchRoomAssignments();
    }
  }, [fetchRoomAssignments, users]);

  // Auto-dismiss success/error messages
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Header scroll effect
  useEffect(() => {
    const handleScroll = () => {
      if (headerRef.current) {
        if (window.scrollY > 20) {
          headerRef.current.style.boxShadow = '0 4px 20px rgba(0,0,0,0.1)';
          headerRef.current.style.backdropFilter = 'blur(12px)';
          headerRef.current.style.backgroundColor = isDarkMode 
            ? 'rgba(15, 23, 42, 0.85)' 
            : 'rgba(255, 255, 255, 0.85)';
        } else {
          headerRef.current.style.boxShadow = 'none';
          headerRef.current.style.backdropFilter = 'none';
          headerRef.current.style.backgroundColor = isDarkMode 
            ? COLORS.dark.surface 
            : COLORS.light.surface;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isDarkMode]);
  
  // Drop zone hover effect
  useEffect(() => {
    if (!fileDropRef.current) return;
    
    const handleDragEnter = (e) => {
      e.preventDefault();
      fileDropRef.current.classList.add('drag-over');
    };
    
    const handleDragLeave = (e) => {
      e.preventDefault();
      fileDropRef.current.classList.remove('drag-over');
    };
    
    const handleDragOver = (e) => {
      e.preventDefault();
    };
    
    const handleDrop = (e) => {
      e.preventDefault();
      fileDropRef.current.classList.remove('drag-over');
      
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        const file = e.dataTransfer.files[0];
        handleFileSelect({ target: { files: [file] } });
      }
    };
    
    const element = fileDropRef.current;
    element.addEventListener('dragenter', handleDragEnter);
    element.addEventListener('dragleave', handleDragLeave);
    element.addEventListener('dragover', handleDragOver);
    element.addEventListener('drop', handleDrop);
    
    return () => {
      element.removeEventListener('dragenter', handleDragEnter);
      element.removeEventListener('dragleave', handleDragLeave);
      element.removeEventListener('dragover', handleDragOver);
      element.removeEventListener('drop', handleDrop);
    };
  }, []);

  // File handling functions
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setError(null);
      setUploadResult(null);
      
      // Display file type info
      const fileExtension = file.name.split('.').pop().toLowerCase();
      let status = '';
      let confidence = 0;
      
      switch (fileExtension) {
        case 'xlsx':
        case 'xls':
          status = 'Excel-Datei erkannt. Die Smart-Verarbeitung kann Zeilen intelligent zuordnen.';
          confidence = 92;
          break;
        case 'csv':
          status = 'CSV-Datei erkannt. Die Smart-Verarbeitung kann Benutzernamen und E-Mails zuordnen.';
          confidence = 85;
          break;
        case 'txt':
          status = 'Text-Datei erkannt. Die Smart-Verarbeitung wird versuchen, Benutzer-Zimmer-Zuordnungen zu extrahieren.';
          confidence = 70;
          break;
        default:
          status = 'Unbekanntes Dateiformat. Die Verarbeitung könnte fehlschlagen.';
          confidence = 40;
      }
      
      setProcessingStatus(status);
      setConfidenceScore(confidence);
      
      // Simulate AI analyzing the file
      setIsAiAnalyzing(true);
      setTimeout(() => {
        setIsAiAnalyzing(false);
        setShowAiSuggestions(true);
      }, 2200);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Bitte wähle zuerst eine Datei aus');
      return;
    }

    setUploadLoading(true);
    setError(null);
    setSuccessMessage('');
    setProcessingProgress(10);
    setShowAiSuggestions(false);
    
    try {
      const token = await localforage.getItem('token');
      if (!token) throw new Error('Kein Token gefunden');

      const formData = new FormData();
      formData.append('file', selectedFile);

      // Use the smart endpoint if smart processing is enabled
      const endpoint = useSmartProcessing ? '/api/rooms/smart-import' : '/api/rooms/import';
      setProcessingProgress(30);
      
      const response = await fetch(`${BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      setProcessingProgress(70);
      
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Fehler beim Hochladen der Datei');
      }

      setProcessingProgress(90);
      setUploadResult(data);
      const successCount = data.successCount || 0;
      const totalProcessed = data.totalProcessed || 0;
      setSuccessMessage(`${successCount} von ${totalProcessed} Zimmerzuweisungen erfolgreich verarbeitet.`);
      setSelectedFile(null);
      
      // Reload room assignments after successful upload
      fetchRoomAssignments();
      // Update the main users list if fetchUsers is available
      if (fetchUsers && typeof fetchUsers === 'function') {
        fetchUsers();
      }
      
      // Reset file input
      const fileInput = document.getElementById('room-file-upload');
      if (fileInput) fileInput.value = '';
      
      setProcessingProgress(100);
      
      // Auto reset progress after 2 seconds
      setTimeout(() => {
        setProcessingProgress(0);
      }, 2000);
      
    } catch (err) {
      console.error('Error uploading file:', err);
      setError(err.message || 'Fehler beim Hochladen der Datei');
      setProcessingProgress(0);
    } finally {
      setUploadLoading(false);
    }
  };

  // Search and filtering
  const handleSearch = (e) => {
    setSearchText(e.target.value);
  };

  const clearSearch = () => {
    setSearchText('');
  };

  const filteredAssignments = roomAssignments.filter(assignment => 
    assignment.username?.toLowerCase().includes(searchText.toLowerCase()) ||
    assignment.email?.toLowerCase().includes(searchText.toLowerCase()) ||
    assignment.roomNumber?.toLowerCase().includes(searchText.toLowerCase()) ||
    (assignment.building && assignment.building.toLowerCase().includes(searchText.toLowerCase()))
  );

  // User management functions
  const getUnassignedUsers = () => {
    if (!Array.isArray(users)) return [];
    const assignedUserIds = roomAssignments.map(assignment => assignment.userId);
    return users.filter(user => !assignedUserIds.includes(user.id));
  };

  const getSortedUnassignedUsers = () => {
    const unassignedUsers = getUnassignedUsers();
    
    return unassignedUsers.sort((a, b) => {
      // Preferred users first
      const aIsPreferred = preferredUsers.includes(a.email);
      const bIsPreferred = preferredUsers.includes(b.email);
      
      if (aIsPreferred && !bIsPreferred) return -1;
      if (!aIsPreferred && bIsPreferred) return 1;
      
      // Then sort alphabetically by username
      return a.username.localeCompare(b.username);
    });
  };

  // UI state management
  const toggleSection = (section) => {
    switch(section) {
      case 'csvFormat':
        setShowCSVFormat(!showCSVFormat);
        break;
      case 'aiSuggestions':
        setShowAiSuggestions(!showAiSuggestions);
        break;
      case 'aiInfo':
        setShowAiInfo(!showAiInfo);
        break;
      default:
        break;
    }
  };

  const toggleExportPanel = () => {
    setShowExportPanel(!showExportPanel);
  };

  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
  };
  
  const toggleViewMode = () => {
    setViewMode(viewMode === 'cards' ? 'table' : 'cards');
  };

  // Form handling
  const validateForm = () => {
    const errors = {};
    
    if (formMode === 'add' && !manualFormData.email) {
      errors.email = 'Bitte wähle einen Benutzer aus';
    }
    
    if (!manualFormData.roomNumber) {
      errors.roomNumber = 'Bitte gib eine Zimmernummer ein';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFormInputChange = (e) => {
    const { name, value } = e.target;
    setManualFormData({
      ...manualFormData,
      [name]: value
    });
    
    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: null
      });
    }
  };

  const openAddForm = () => {
    setManualFormData({ userId: null, email: '', roomNumber: '', building: '' });
    setFormMode('add');
    setShowManualForm(true);
    setFormErrors({});
  };

  const openEditForm = (assignment) => {
    setManualFormData({
      userId: assignment.userId,
      email: assignment.email,
      roomNumber: assignment.roomNumber,
      building: assignment.building || ''
    });
    setFormMode('edit');
    setShowManualForm(true);
    setFormErrors({});
  };

  const handleQuickAssignUser = (user) => {
    setManualFormData({
      userId: user.id,
      email: user.email,
      roomNumber: '',
      building: ''
    });
    setFormMode('add');
    setShowManualForm(true);
    setFormErrors({});
  };

  const handleSubmitManualForm = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setError(null);
    setSuccessMessage('');
    
    try {
      const token = await localforage.getItem('token');
      if (!token) throw new Error('Kein Token gefunden');

      let email = manualFormData.email;
      
      // For edit mode without email, use existing assignment email
      if (formMode === 'edit' && !email) {
        const existingAssignment = roomAssignments.find(
          a => a.userId === manualFormData.userId
        );
        if (existingAssignment) {
          email = existingAssignment.email;
        }
      }

      // Create request body
      const requestBody = {
        email: email,
        roomNumber: manualFormData.roomNumber,
        building: manualFormData.building
      };

      // Make API call
      const response = await fetch(`${BASE_URL}/api/rooms/assign`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Fehler beim Zuweisen des Zimmers');
      }

      // Success
      setSuccessMessage(
        formMode === 'add'
          ? 'Zimmerzuweisung erfolgreich hinzugefügt'
          : 'Zimmerzuweisung erfolgreich aktualisiert'
      );
      
      // Close form and refresh data
      setShowManualForm(false);
      fetchRoomAssignments();
      // Update main users list if available
      if (fetchUsers && typeof fetchUsers === 'function') {
        fetchUsers();
      }
    } catch (err) {
      console.error('Error assigning room:', err);
      setError(err.message || 'Fehler beim Zuweisen des Zimmers');
    }
  };

  const handleDeleteAssignment = async (assignment) => {
    if (!window.confirm(`Bist du sicher, dass du die Zimmerzuweisung für ${assignment.username} (Zimmer ${assignment.roomNumber}) löschen möchtest?`)) {
      return;
    }
    
    setError(null);
    setSuccessMessage('');
    
    try {
      const token = await localforage.getItem('token');
      if (!token) throw new Error('Kein Token gefunden');

      const response = await fetch(`${BASE_URL}/api/rooms/unassign`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: assignment.email })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Fehler beim Löschen der Zimmerzuweisung');
      }

      // Success
      setSuccessMessage('Zimmerzuweisung erfolgreich gelöscht');
      
      // Refresh data
      fetchRoomAssignments();
      // Update main users list if available
      if (fetchUsers && typeof fetchUsers === 'function') {
        fetchUsers();
      }
    } catch (err) {
      console.error('Error deleting room assignment:', err);
      setError(err.message || 'Fehler beim Löschen der Zimmerzuweisung');
    }
  };

  // Export functions
  const generateExportData = () => {
    if (exportFormat === 'csv') {
      let csvContent = 'Name,Email,Zimmer,Gebäude\n';
      filteredAssignments.forEach(assignment => {
        const building = assignment.building || '';
        csvContent += `${assignment.username},${assignment.email},${assignment.roomNumber},"${building}"\n`;
      });
      return csvContent;
    } else if (exportFormat === 'text') {
      let textContent = '';
      filteredAssignments.forEach(assignment => {
        const building = assignment.building ? `(${assignment.building})` : '';
        textContent += `${assignment.username} - Zimmer ${assignment.roomNumber} ${building}\n`;
      });
      return textContent;
    } else if (exportFormat === 'list') {
      let listContent = '';
      filteredAssignments.forEach(assignment => {
        const building = assignment.building ? ` - ${assignment.building}` : '';
        listContent += `${assignment.username}\t${assignment.roomNumber}${building}\n`;
      });
      return listContent;
    }
    return '';
  };

  const downloadExportData = () => {
    const exportData = generateExportData();
    const blob = new Blob([exportData], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `zimmerliste.${exportFormat}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const copyToClipboard = () => {
    const exportData = generateExportData();
    navigator.clipboard.writeText(exportData)
      .then(() => {
        setCopySuccess('In die Zwischenablage kopiert!');
        setTimeout(() => setCopySuccess(''), 3000);
      })
      .catch(err => {
        console.error('Fehler beim Kopieren:', err);
        setCopySuccess('Fehler beim Kopieren!');
      });
  };

  // Utility functions
  const getFileIcon = (filename) => {
    if (!filename) return <FileText size={18} />;
    
    const extension = filename.split('.').pop().toLowerCase();
    switch (extension) {
      case 'xlsx':
      case 'xls':
        return <FileSpreadsheet size={18} />;
      case 'csv':
        return <FileSpreadsheet size={18} />;
      case 'txt':
        return <FileText size={18} />;
      default:
        return <FileText size={18} />;
    }
  };

  const findUserByEmail = (email) => {
    if (!Array.isArray(users)) return null;
    return users.find(user => user.email === email);
  };

  // Calculate AI suggestions based on data
  const getAiSuggestions = () => {
    const unassignedCount = getUnassignedUsers().length;
    
    const suggestions = [];
    
    if (unassignedCount > 0) {
      suggestions.push({
        id: 'unassigned',
        message: `${unassignedCount} Lernende haben noch kein Zimmer zugewiesen.`,
        action: 'Zur Übersicht wechseln',
        handler: () => setActiveTab('unassigned'),
        priority: 'high'
      });
    }
    
    const usersWithoutBuilding = roomAssignments.filter(a => !a.building || a.building.trim() === '').length;
    if (usersWithoutBuilding > 0) {
      suggestions.push({
        id: 'missing-buildings',
        message: `${usersWithoutBuilding} Zimmerzuweisungen haben keine Gebäudeinformation.`,
        action: 'Tabelle anzeigen',
        handler: () => {
          setActiveTab('assigned');
          setViewMode('table');
        },
        priority: 'medium'
      });
    }
    
    if (selectedFile) {
      const extension = selectedFile.name.split('.').pop().toLowerCase();
      if (extension === 'xlsx' || extension === 'xls') {
        suggestions.push({
          id: 'excel-detection',
          message: 'Excel-Datei erkannt. Smart-Verarbeitung kann komplexe Formate und mehrere Tabellenblätter verarbeiten.',
          action: 'Starten',
          handler: handleUpload,
          priority: 'high'
        });
      }
    }
    
    // Add recommendation for roommate optimization if there are enough assignments
    if (roomAssignments.length > 10) {
      suggestions.push({
        id: 'optimize-roommates',
        message: 'Optimierungspotential erkannt: Zimmer könnten effizienter nach Präferenzen und Profilen zugeteilt werden.',
        action: 'Mehr erfahren',
        handler: () => toggleSection('aiInfo'),
        priority: 'low'
      });
    }
    
    return suggestions;
  };

  // Color getters for current theme
  const getColor = (key) => {
    return isDarkMode ? COLORS.dark[key] : COLORS.light[key];
  };

  // Render subsections
  const renderNotificationBanner = () => {
    if (!successMessage && !error) return null;
    
    return (
      <div 
        className="notification-banner"
        style={{
          position: 'sticky',
          top: '0',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '16px 20px',
          borderRadius: '14px',
          marginBottom: '24px',
          fontSize: '15px',
          fontWeight: '500',
          zIndex: '10',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          backgroundColor: successMessage 
            ? isDarkMode ? 'rgba(16, 185, 129, 0.2)' : '#ecfdf5'
            : isDarkMode ? 'rgba(239, 68, 68, 0.2)' : '#fef2f2',
          color: successMessage 
            ? isDarkMode ? COLORS.dark.success : COLORS.light.success
            : isDarkMode ? COLORS.dark.error : COLORS.light.error,
          borderLeft: `4px solid ${successMessage 
            ? isDarkMode ? COLORS.dark.success : COLORS.light.success
            : isDarkMode ? COLORS.dark.error : COLORS.light.error}`
        }}
      >
        {successMessage && (
          <>
            <CheckCircle size={20} />
            <span>{successMessage}</span>
          </>
        )}
        {error && (
          <>
            <AlertCircle size={20} />
            <span>{error}</span>
          </>
        )}
        <button 
          style={{
            marginLeft: 'auto',
            background: 'none',
            border: 'none',
            color: 'inherit',
            cursor: 'pointer',
            padding: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            transition: 'background-color 0.2s ease',
            '&:hover': {
              backgroundColor: 'rgba(0,0,0,0.05)',
            }
          }}
          onClick={() => {
            if (successMessage) setSuccessMessage('');
            if (error) setError(null);
          }}
        >
          <X size={18} />
        </button>
      </div>
    );
  };

  const renderUploadSection = () => (
    <div className="upload-section" style={{
      marginBottom: '40px',
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px',
      }}>
        <h3 style={{
          fontSize: '22px',
          fontWeight: '600',
          color: getColor('text'),
          margin: 0,
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '10px',
            borderRadius: '12px',
            backgroundColor: isDarkMode ? 'rgba(99, 102, 241, 0.2)' : '#ede9fe',
            color: getColor('primary'),
          }}>
            <Sparkles size={20} />
          </div>
          Intelligente Dateiverarbeitung für Zimmerzuweisungen
          
          <button
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '6px',
              borderRadius: '50%',
              cursor: 'pointer',
              color: getColor('textLight'),
              transition: 'all 0.2s ease',
              '&:hover': {
                color: getColor('primary'),
                backgroundColor: isDarkMode ? 'rgba(99, 102, 241, 0.1)' : '#ede9fe',
              }
            }}
            onClick={() => toggleSection('aiInfo')}
            title="KI Informationen"
          >
            <Info size={16} />
          </button>
        </h3>
      </div>
      
      {showAiInfo && (
        <div className="ai-info-panel" style={{
          backgroundColor: isDarkMode ? 'rgba(99, 102, 241, 0.1)' : '#f5f3ff',
          borderRadius: '16px',
          padding: '20px',
          marginBottom: '24px',
          border: `1px solid ${isDarkMode ? 'rgba(99, 102, 241, 0.2)' : '#ddd6fe'}`,
          boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)',
          animation: 'fadeIn 0.3s ease-out',
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '16px',
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '10px',
                backgroundColor: isDarkMode ? 'rgba(99, 102, 241, 0.2)' : '#ede9fe',
                borderRadius: '12px',
                color: getColor('primary'),
              }}>
                <Brain size={20} />
              </div>
              <div>
                <h4 style={{
                  margin: '0 0 4px 0',
                  fontSize: '18px',
                  fontWeight: '600',
                  color: getColor('text'),
                }}>KI-Assistent für Zimmerverwaltung</h4>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}>
                  <span style={{
                    fontSize: '13px',
                    color: getColor('textLight'),
                  }}>Version {aiModelVersion}</span>
                  <span style={{
                    fontSize: '13px',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    backgroundColor: isDarkMode ? 'rgba(236, 72, 153, 0.2)' : '#fce7f3',
                    color: isDarkMode ? COLORS.dark.secondary : COLORS.light.secondary,
                  }}>Beta</span>
                  <span style={{
                    fontSize: '13px',
                    color: getColor('textLight'),
                  }}>Aktualisiert: {aiUpdateDate}</span>
                </div>
              </div>
            </div>
            <button
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '8px',
                borderRadius: '8px',
                cursor: 'pointer',
                color: getColor('textLight'),
                transition: 'all 0.2s ease',
                '&:hover': {
                  color: getColor('text'),
                  backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                }
              }}
              onClick={() => toggleSection('aiInfo')}
            >
              <X size={18} />
            </button>
          </div>
          
          <div style={{
            backgroundColor: isDarkMode ? 'rgba(29, 35, 42, 0.5)' : 'rgba(255, 255, 255, 0.7)',
            borderRadius: '12px',
            padding: '16px',
            fontSize: '14px',
            lineHeight: '1.6',
            color: getColor('textMedium'),
          }}>
            <p style={{ marginTop: 0 }}>
              Unser KI-System ist darauf spezialisiert, komplexe Muster in Daten zu erkennen und 
              kann verschiedene Dateiformate (Excel, CSV, TXT) verarbeiten, um Benutzerdaten und Zimmerzuweisungen zu extrahieren.
            </p>
            <div style={{
              display: 'flex',
              gap: '20px',
              flexWrap: 'wrap',
              marginTop: '16px',
            }}>
              <div style={{
                flex: '1 1 280px',
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
              }}>
                <span style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontWeight: '500',
                  color: getColor('text'),
                }}>
                  <Shield size={16} color={getColor('success')} /> Unterstützte Funktionen
                </span>
                <ul style={{
                  margin: '8px 0',
                  paddingLeft: '24px',
                }}>
                  <li>Intelligente Tabellenerkennung</li>
                  <li>Format- und Spaltenerkennung</li>
                  <li>Benutzer-Zimmer Zuordnung</li>
                  <li>Namens- und E-Mail-Matching</li>
                </ul>
              </div>
              <div style={{
                flex: '1 1 280px',
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
              }}>
                <span style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontWeight: '500',
                  color: getColor('text'),
                }}>
                  <AlertCircle size={16} color={getColor('warning')} /> Einschränkungen
                </span>
                <ul style={{
                  margin: '8px 0',
                  paddingLeft: '24px',
                }}>
                  <li>Interpretation von Freitext kann ungenau sein</li>
                  <li>Bei großen Dateien kann die Verarbeitung länger dauern</li>
                  <li>Komplexe Formate brauchen eventuell manuelle Überprüfung</li>
                  <li>KI-Vorschläge sind noch in Entwicklung</li>
                </ul>
              </div>
            </div>
            <div style={{
              marginTop: '16px',
              padding: '12px',
              borderRadius: '8px',
              backgroundColor: isDarkMode ? 'rgba(99, 102, 241, 0.1)' : '#ede9fe',
              border: `1px solid ${isDarkMode ? 'rgba(99, 102, 241, 0.2)' : '#ddd6fe'}`,
            }}>
              <span style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '13px',
                color: getColor('primary'),
              }}>
                <Info size={15} /> 
                Diese KI-Funktionen befinden sich noch in der Betaphase. Bei wichtigen Entscheidungen 
                empfehlen wir eine manuelle Überprüfung der Ergebnisse.
              </span>
            </div>
          </div>
        </div>
      )}
      
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        alignItems: 'center',
      }}>
        <div 
          ref={fileDropRef}
          className="file-drop-zone"
          style={{
            width: '100%',
            maxWidth: '740px',
          }}
        >
          <input
            id="room-file-upload"
            type="file"
            accept=".csv,.xls,.xlsx,.txt"
            onChange={handleFileSelect}
            style={{
              display: 'none',
            }}
          />
          <label 
            htmlFor="room-file-upload" 
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '20px',
              padding: '48px 40px',
              backgroundColor: isDarkMode ? 'rgba(30, 41, 59, 0.5)' : '#f8fafc',
              border: `2px dashed ${isDarkMode ? getColor('borderAlt') : getColor('border')}`,
              borderRadius: '20px',
              cursor: 'pointer',
              color: getColor('textLight'),
              transition: 'all 0.3s ease',
              '&:hover': {
                borderColor: getColor('primary'),
                backgroundColor: isDarkMode ? 'rgba(99, 102, 241, 0.05)' : '#f0f7ff',
                boxShadow: `0 4px 20px rgba(99, 102, 241, 0.1)`,
              },
            }}
          >
            <div style={{
              display: 'flex',
              gap: '20px',
            }}>
              <FileSpreadsheet size={40} style={{
                color: getColor('primary'),
                opacity: 0.8,
              }} />
              <FileText size={40} style={{
                color: getColor('primary'),
                opacity: 0.8,
              }} />
              <FileSpreadsheet size={40} style={{
                color: getColor('primary'),
                opacity: 0.8,
              }} />
            </div>
            <span style={{
              fontSize: '18px',
              fontWeight: '500',
              color: getColor('textMedium'),
            }}>
              Excel, CSV oder Text-Datei hier ablegen oder klicken zum Auswählen
            </span>
            <span style={{
              fontSize: '15px',
              color: getColor('textLight'),
              maxWidth: '600px',
              textAlign: 'center',
              lineHeight: '1.6',
            }}>
              Die Datei sollte Benutzerdaten (Name oder E-Mail), Zimmernummern und optional Gebäude enthalten.
              Unser KI-System erkennt automatisch verschiedene Formate und Layouts.
            </span>
          </label>
        </div>
        
        {selectedFile && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            maxWidth: '740px',
            padding: '20px 24px',
            backgroundColor: isDarkMode ? 'rgba(6, 182, 212, 0.1)' : '#ecfeff',
            borderRadius: '16px',
            boxShadow: `0 4px 15px rgba(6, 182, 212, ${isDarkMode ? '0.15' : '0.07'})`,
            animation: 'fadeIn 0.3s ease-out',
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              fontSize: '16px',
              color: isDarkMode ? COLORS.dark.accent : COLORS.light.accent,
            }}>
              {getFileIcon(selectedFile.name)}
              <span style={{ fontWeight: '500' }}>{selectedFile.name}</span>
              <span style={{
                fontSize: '13px',
                color: getColor('textLight'),
                marginLeft: '4px',
              }}>
                ({Math.round(selectedFile.size / 1024)} KB)
              </span>
            </div>
            <button
              onClick={() => setSelectedFile(null)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '8px',
                backgroundColor: isDarkMode ? 'rgba(6, 182, 212, 0.15)' : 'rgba(6, 182, 212, 0.1)',
                border: 'none',
                borderRadius: '10px',
                color: isDarkMode ? COLORS.dark.accent : COLORS.light.accent,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: isDarkMode ? 'rgba(6, 182, 212, 0.25)' : 'rgba(6, 182, 212, 0.2)',
                },
              }}
            >
              <X size={16} />
            </button>
          </div>
        )}
        
        {isAiAnalyzing && (
          <div className="ai-analyzing" style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '16px',
            width: '100%',
            maxWidth: '740px',
            padding: '20px 24px',
            backgroundColor: isDarkMode ? 'rgba(99, 102, 241, 0.1)' : '#f5f3ff',
            borderRadius: '16px',
            boxShadow: `0 4px 15px rgba(99, 102, 241, ${isDarkMode ? '0.15' : '0.07'})`,
            animation: 'fadeIn 0.3s ease-out',
          }}>
            <div className="pulse-loader" style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '12px',
              backgroundColor: isDarkMode ? 'rgba(99, 102, 241, 0.15)' : '#ede9fe',
              borderRadius: '14px',
              color: getColor('primary'),
              animation: 'pulse 1.5s ease-in-out infinite',
            }}>
              <Brain size={24} />
            </div>
            <div style={{
              flex: 1,
            }}>
              <div style={{
                fontSize: '16px',
                fontWeight: '600',
                color: getColor('primary'),
                marginBottom: '4px',
              }}>
                KI analysiert Datei...
              </div>
              <div style={{
                fontSize: '14px',
                color: getColor('textMedium'),
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                }}>
                  <span>Struktur erkennen</span>
                  <span>√</span>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                }}>
                  <span>Datenformat identifizieren</span>
                  <span>√</span>
                </div>
                <div className="typing-dots" style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                }}>
                  <span>Muster und Beziehungen analysieren</span>
                  <span className="dots">•••</span>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {processingStatus && !isAiAnalyzing && (
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '16px',
            width: '100%',
            maxWidth: '740px',
            padding: '20px 24px',
            backgroundColor: isDarkMode ? 'rgba(99, 102, 241, 0.1)' : '#f5f3ff',
            borderRadius: '16px',
            boxShadow: `0 4px 15px rgba(99, 102, 241, ${isDarkMode ? '0.15' : '0.07'})`,
            animation: 'fadeIn 0.3s ease-out',
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '12px',
              backgroundColor: isDarkMode ? 'rgba(99, 102, 241, 0.15)' : '#ede9fe',
              borderRadius: '14px',
              color: getColor('primary'),
            }}>
              <Brain size={22} />
            </div>
            <div style={{
              flex: 1,
            }}>
              <div style={{
                fontSize: '15px',
                lineHeight: '1.5',
                color: getColor('textMedium'),
                marginBottom: '12px',
              }}>
                {processingStatus}
              </div>
              {confidenceScore > 0 && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                }}>
                  <div style={{
                    fontSize: '13px',
                    color: getColor('textLight'),
                    minWidth: '100px',
                  }}>
                    KI-Konfidenz
                  </div>
                  <div style={{
                    flex: 1,
                    height: '8px',
                    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                    borderRadius: '4px',
                    overflow: 'hidden',
                  }}>
                    <div style={{
                      height: '100%',
                      width: `${confidenceScore}%`,
                      backgroundColor: confidenceScore > 80 
                        ? getColor('success') 
                        : confidenceScore > 50 
                          ? getColor('warning') 
                          : getColor('error'),
                      borderRadius: '4px',
                      transition: 'width 0.8s cubic-bezier(0.65, 0, 0.35, 1)',
                    }}></div>
                  </div>
                  <div style={{
                    fontSize: '13px',
                    fontWeight: '600',
                    color: getColor('textMedium'),
                    minWidth: '45px',
                  }}>
                    {confidenceScore}%
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        
        {showAiSuggestions && !isAiAnalyzing && (
          <div style={{
            width: '100%',
            maxWidth: '740px',
            backgroundColor: isDarkMode ? 'rgba(6, 182, 212, 0.1)' : '#ecfeff',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: `0 4px 15px rgba(6, 182, 212, ${isDarkMode ? '0.15' : '0.07'})`,
            animation: 'fadeIn 0.3s ease-out',
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px 24px',
              borderBottom: `1px solid ${isDarkMode ? 'rgba(6, 182, 212, 0.2)' : '#bae6fd'}`,
              cursor: 'pointer',
            }}
            onClick={() => toggleSection('aiSuggestions')}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                fontSize: '16px',
                fontWeight: '600',
                color: isDarkMode ? COLORS.dark.accent : COLORS.light.accent,
              }}>
                <Sparkles size={18} style={{
                  color: isDarkMode ? COLORS.dark.accent : COLORS.light.accent,
                }} />
                <span>KI-Empfehlungen</span>
              </div>
              {showAiSuggestions ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </div>
            
            <div style={{
              padding: '16px',
            }}>
              {getAiSuggestions().map((suggestion, index) => (
                <div 
                  key={suggestion.id} 
                  ref={el => suggestionRefs.current[index] = el}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '16px 20px',
                    backgroundColor: isDarkMode ? 'rgba(15, 23, 42, 0.3)' : '#ffffff',
                    borderRadius: '12px',
                    marginBottom: '10px',
                    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.05)',
                    borderLeft: `3px solid ${
                      suggestion.priority === 'high' 
                        ? getColor('error')
                        : suggestion.priority === 'medium'
                          ? getColor('warning')
                          : getColor('success')
                    }`,
                    animation: 'fadeInUp 0.5s ease-out forwards',
                    animationDelay: `${index * 0.1}s`,
                    opacity: 0,
                    transform: 'translateY(10px)',
                  }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                  }}>
                    <Zap size={18} style={{
                      color: isDarkMode ? COLORS.dark.accent : COLORS.light.accent,
                    }} />
                    <span style={{
                      fontSize: '14px',
                      color: getColor('textMedium'),
                    }}>{suggestion.message}</span>
                  </div>
                  <button 
                    onClick={suggestion.handler}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '8px 14px',
                      backgroundColor: isDarkMode ? 'rgba(6, 182, 212, 0.2)' : '#cffafe',
                      color: isDarkMode ? COLORS.dark.accent : COLORS.light.accent,
                      border: 'none',
                      borderRadius: '10px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: isDarkMode ? 'rgba(6, 182, 212, 0.3)' : '#a5f3fc',
                        transform: 'translateY(-2px)',
                      },
                      '&:active': {
                        transform: 'translateY(0)',
                      },
                    }}
                  >
                    <span>{suggestion.action}</span>
                    <ArrowUpRight size={14} />
                  </button>
                </div>
              ))}

              {getAiSuggestions().length === 0 && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  padding: '24px 16px',
                  color: getColor('textLight'),
                  fontSize: '15px',
                  textAlign: 'center',
                }}>
                  <HelpCircle size={20} />
                  <span>Keine spezifischen Empfehlungen für die aktuelle Datei verfügbar</span>
                </div>
              )}
            </div>
          </div>
        )}
        
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          width: '100%',
          maxWidth: '740px',
          padding: '20px 24px',
          backgroundColor: isDarkMode ? 'rgba(99, 102, 241, 0.05)' : '#fafaff',
          borderRadius: '16px',
          border: `1px solid ${isDarkMode ? 'rgba(99, 102, 241, 0.1)' : '#e0e7ff'}`,
        }}>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            cursor: 'pointer',
          }}>
            <input
              type="checkbox"
              checked={useSmartProcessing}
              onChange={(e) => setUseSmartProcessing(e.target.checked)}
              style={{
                width: '18px',
                height: '18px',
                cursor: 'pointer',
                accentColor: getColor('primary'),
              }}
            />
            <span style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              fontSize: '15px',
              fontWeight: '500',
              color: getColor('primary'),
            }}>
              <Brain size={16} />
              Smart-Verarbeitung aktivieren
            </span>
          </label>
          <div style={{
            fontSize: '14px',
            lineHeight: '1.5',
            color: getColor('textLight'),
            marginLeft: '30px',
          }}>
            Intelligente Verarbeitung erkennt verschiedene Formate, versteht komplexe Strukturen und 
            ordnet Daten selbst dann korrekt zu, wenn sie in ungewöhnlichen Layouts vorliegen.
          </div>
        </div>
        
        {processingProgress > 0 && (
          <div style={{
            width: '100%',
            maxWidth: '740px',
            height: '8px',
            backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
            borderRadius: '4px',
            overflow: 'hidden',
          }}>
            <div 
              style={{
                height: '100%',
                width: `${processingProgress}%`,
                backgroundColor: getColor('success'),
                borderRadius: '4px',
                transition: 'width 0.3s ease',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <div className="progress-shimmer" style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                background: `linear-gradient(90deg, 
                  transparent, 
                  ${isDarkMode ? 'rgba(255, 255, 255, 0.15)' : 'rgba(255, 255, 255, 0.6)'}, 
                  transparent)`,
                animation: 'shimmer 1.5s infinite',
              }}></div>
            </div>
          </div>
        )}
        
        <button 
          onClick={handleUpload} 
          disabled={!selectedFile || uploadLoading}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '14px 28px',
            backgroundColor: getColor('primary'),
            color: '#ffffff',
            border: 'none',
            borderRadius: '14px',
            fontSize: '16px',
            fontWeight: '500',
            cursor: !selectedFile || uploadLoading ? 'not-allowed' : 'pointer',
            boxShadow: `0 4px 14px rgba(99, 102, 241, ${isDarkMode ? '0.3' : '0.2'})`,
            transition: 'all 0.3s ease',
            opacity: !selectedFile || uploadLoading ? 0.7 : 1,
            '&:hover': {
              backgroundColor: getColor('primaryDark'),
              transform: 'translateY(-2px)',
              boxShadow: `0 8px 20px rgba(99, 102, 241, ${isDarkMode ? '0.4' : '0.3'})`,
            },
            '&:active': {
              transform: 'translateY(0)',
            },
          }}
        >
          {uploadLoading ? (
            <>
              <div className="spinner" style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                animation: 'spin 1s linear infinite',
              }}>
                <RefreshCw size={18} />
              </div>
              <span>Verarbeiten...</span>
            </>
          ) : (
            <>
              <Upload size={18} />
              <span>Datei hochladen und verarbeiten</span>
            </>
          )}
        </button>
      </div>

      {uploadResult && uploadResult.failCount > 0 && (
        <div style={{
          marginTop: '32px',
          backgroundColor: isDarkMode ? 'rgba(239, 68, 68, 0.1)' : '#fff1f2',
          borderRadius: '16px',
          padding: '20px 24px',
          maxWidth: '740px',
          margin: '0 auto',
          boxShadow: `0 4px 15px rgba(239, 68, 68, ${isDarkMode ? '0.15' : '0.07'})`,
          animation: 'fadeIn 0.3s ease-out',
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
            borderBottom: `1px solid ${isDarkMode ? 'rgba(239, 68, 68, 0.2)' : '#fecdd3'}`,
            paddingBottom: '10px',
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
            }}>
              <AlertCircle size={20} style={{
                color: getColor('error'),
              }} />
              <h4 style={{
                fontSize: '17px',
                fontWeight: '600',
                color: getColor('error'),
                margin: 0,
              }}>Nicht verarbeitete Einträge</h4>
            </div>
            <span style={{
              padding: '4px 10px',
              backgroundColor: isDarkMode ? 'rgba(239, 68, 68, 0.15)' : '#fee2e2',
              borderRadius: '12px',
              fontSize: '13px',
              color: getColor('error'),
              fontWeight: '500',
            }}>
              {uploadResult.failCount} von {uploadResult.totalProcessed}
            </span>
          </div>
          <div style={{
            maxHeight: '220px',
            overflowY: 'auto',
            padding: '4px',
          }}>
            {uploadResult.failedEntries.map((entry, idx) => (
              <div 
                key={idx} 
                style={{
                  padding: '12px 16px',
                  backgroundColor: isDarkMode ? 'rgba(15, 23, 42, 0.3)' : '#ffffff',
                  borderRadius: '10px',
                  marginBottom: '8px',
                  fontSize: '14px',
                  color: getColor('textMedium'),
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                  animation: 'fadeInUp 0.3s ease-out forwards',
                  animationDelay: `${idx * 0.05}s`,
                  opacity: 0,
                  transform: 'translateY(10px)',
                }}
              >
                {entry}
              </div>
            ))}
          </div>
          <div style={{
            marginTop: '16px',
            padding: '12px 16px',
            backgroundColor: isDarkMode ? 'rgba(15, 23, 42, 0.3)' : '#ffffff',
            borderRadius: '10px',
            fontSize: '13px',
            color: getColor('textLight'),
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}>
            <Info size={16} />
            <span>
              Tipp: Prüfe ob die E-Mail-Adressen korrekt sind und ob die Benutzer im System existieren.
            </span>
          </div>
        </div>
      )}

      <div style={{
        marginTop: '32px',
      }}>
        <div style={{
          backgroundColor: isDarkMode ? 'rgba(30, 41, 59, 0.5)' : '#f8fafc',
          borderRadius: '16px',
          padding: '20px',
          maxWidth: '800px',
          margin: '0 auto',
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'pointer',
            padding: '4px 0',
          }}
          onClick={() => toggleSection('csvFormat')}>
            <h4 style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              fontSize: '17px',
              fontWeight: '600',
              color: getColor('text'),
              margin: 0,
            }}>
              <FileSpreadsheet size={18} />
              Unterstützte Dateiformate
            </h4>
            {showCSVFormat ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </div>
          
          {showCSVFormat && (
            <div className="format-content" style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              marginTop: '20px',
              animation: 'fadeIn 0.3s ease-out',
            }}>
              <div style={{
                backgroundColor: isDarkMode ? COLORS.dark.surfaceAlt : '#ffffff',
                borderRadius: '12px',
                padding: '20px 24px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
              }}>
                <h5 style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: getColor('text'),
                  margin: '0 0 12px 0',
                }}>
                  <FileSpreadsheet size={18} style={{ color: getColor('primary') }} />
                  Excel (.xlsx, .xls)
                </h5>
                <p style={{
                  fontSize: '14px',
                  lineHeight: '1.6',
                  color: getColor('textMedium'),
                  margin: '0 0 12px 0',
                }}>
                  Excel-Dateien können mehrere Spalten und Tabellenblätter enthalten. Die Smart-Verarbeitung erkennt automatisch, 
                  welche Spalten Benutzerdaten und Zimmernummern enthalten, sogar bei komplexen oder unregelmäßigen Layouts.
                </p>
              </div>
              
              <div style={{
                backgroundColor: isDarkMode ? COLORS.dark.surfaceAlt : '#ffffff',
                borderRadius: '12px',
                padding: '20px 24px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
              }}>
                <h5 style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: getColor('text'),
                  margin: '0 0 12px 0',
                }}>
                  <FileSpreadsheet size={18} style={{ color: getColor('primary') }} />
                  CSV (.csv)
                </h5>
                <p style={{
                  fontSize: '14px',
                  lineHeight: '1.6',
                  color: getColor('textMedium'),
                  margin: '0 0 12px 0',
                }}>
                  CSV-Dateien werden intelligent analysiert. Idealerweise folgen sie diesem Format:
                </p>
                <div style={{
                  backgroundColor: isDarkMode ? 'rgba(15, 23, 42, 0.5)' : '#f9fafb',
                  padding: '16px',
                  borderRadius: '10px',
                  fontFamily: 'monospace',
                  fontSize: '13px',
                  lineHeight: '1.7',
                  whiteSpace: 'pre',
                  overflowX: 'auto',
                  color: getColor('textMedium'),
                }}>
                  <code>username,room,building</code><br />
                  <code>neo.stutz@sunrise.net,101,Haupthaus</code><br />
                  <code>roman.stammbacj@sunrise.net,102,Nebengebäude</code>
                </div>
              </div>
              
              <div style={{
                backgroundColor: isDarkMode ? COLORS.dark.surfaceAlt : '#ffffff',
                borderRadius: '12px',
                padding: '20px 24px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
              }}>
                <h5 style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: getColor('text'),
                  margin: '0 0 12px 0',
                }}>
                  <FileText size={18} style={{ color: getColor('primary') }} />
                  Text (.txt)
                </h5>
                <p style={{
                  fontSize: '14px',
                  lineHeight: '1.6',
                  color: getColor('textMedium'),
                  margin: '0 0 12px 0',
                }}>
                  Text-Dateien können unstrukturierte Daten enthalten. Die Smart-Verarbeitung 
                  versucht, Benutzer-Zimmer-Zuordnungen zu erkennen, selbst in frei formuliertem Text.
                </p>
                <div style={{
                  backgroundColor: isDarkMode ? 'rgba(15, 23, 42, 0.5)' : '#f9fafb',
                  padding: '16px',
                  borderRadius: '10px',
                  fontFamily: 'monospace',
                  fontSize: '13px',
                  lineHeight: '1.7',
                  whiteSpace: 'pre',
                  overflowX: 'auto',
                  color: getColor('textMedium'),
                }}>
                  <code>neo.stutz@sunrise.net - Zimmer 101</code><br />
                  <code>Roman Stammbacj hat Zimmer 102 im Nebengebäude</code>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderUnassignedUsersSection = () => (
    <div style={{
      marginBottom: '40px',
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px',
      }}>
        <h3 style={{
          fontSize: '22px',
          fontWeight: '600',
          color: getColor('text'),
          margin: 0,
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '10px',
            borderRadius: '12px',
            backgroundColor: isDarkMode ? 'rgba(236, 72, 153, 0.1)' : '#fce7f3',
            color: getColor('secondary'),
          }}>
            <Users size={20} />
          </div>
          Lernende ohne Zimmer
        </h3>
        <div style={{
          display: 'flex',
          gap: '12px',
        }}>
          <button
            onClick={toggleViewMode}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '10px',
              backgroundColor: 'transparent',
              color: getColor('textLight'),
              border: `1px solid ${getColor('border')}`,
              borderRadius: '12px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                color: getColor('text'),
                borderColor: getColor('borderAlt'),
              },
            }}
            title={viewMode === 'cards' ? 'Tabelle anzeigen' : 'Karten anzeigen'}
          >
            {viewMode === 'cards' ? <AlignJustify size={18} /> : <LayoutGrid size={18} />}
          </button>
          <button 
            onClick={openAddForm} 
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 18px',
              backgroundColor: getColor('success'),
              color: '#ffffff',
              border: 'none',
              borderRadius: '12px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              boxShadow: `0 4px 12px rgba(16, 185, 129, ${isDarkMode ? '0.3' : '0.2'})`,
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: isDarkMode ? '#059669' : '#059669',
                transform: 'translateY(-2px)',
                boxShadow: `0 6px 15px rgba(16, 185, 129, ${isDarkMode ? '0.4' : '0.25'})`,
              },
              '&:active': {
                transform: 'translateY(0)',
              },
            }}
          >
            <Plus size={16} />
            <span>Neue Zuweisung</span>
          </button>
        </div>
      </div>

      {viewMode === 'cards' ? (
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '20px',
        }}>
          {getSortedUnassignedUsers().map(user => (
            <div 
              key={user.id} 
              className="user-card"
              style={{
                flex: '1 1 280px',
                maxWidth: '340px',
                backgroundColor: isDarkMode ? COLORS.dark.surfaceAlt : '#ffffff',
                borderRadius: '16px',
                padding: '24px',
                boxShadow: `0 4px 15px rgba(0, 0, 0, ${isDarkMode ? '0.15' : '0.05'})`,
                border: `1px solid ${isDarkMode ? 'transparent' : getColor('border')}`,
                display: 'flex',
                flexDirection: 'column',
                gap: '20px',
                transition: 'all 0.3s ease',
                ...(preferredUsers.includes(user.email) 
                  ? {
                      borderLeft: `4px solid ${getColor('primary')}`,
                      backgroundColor: isDarkMode ? 'rgba(99, 102, 241, 0.08)' : '#f5f3ff',
                    } 
                  : {}),
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 10px 20px rgba(0, 0, 0, ${isDarkMode ? '0.25' : '0.1'})`,
                },
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
              }}>
                <div style={{
                  width: '50px',
                  height: '50px',
                  borderRadius: '25px',
                  overflow: 'hidden',
                  backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#f1f5f9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: `0 2px 6px rgba(0, 0, 0, ${isDarkMode ? '0.2' : '0.05'})`,
                }}>
                  {user.profileImagePath ? (
                    <img 
                      src={user.profileImagePath.startsWith('http') ? user.profileImagePath : `${BASE_URL}${user.profileImagePath}`} 
                      alt={user.username} 
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }} 
                      onError={(e) => { e.target.src = '/api/placeholder/100/100'; }}
                    />
                  ) : (
                    <div style={{
                      fontSize: '18px',
                      fontWeight: '600',
                      color: getColor('textLight'),
                    }}>
                      {user.username.split(' ').map(name => name[0]).join('')}
                    </div>
                  )}
                </div>
                <div style={{
                  flex: 1,
                }}>
                  <h4 style={{
                    margin: '0 0 6px 0',
                    fontSize: '18px',
                    fontWeight: '600',
                    color: getColor('text'),
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}>
                    {preferredUsers.includes(user.email) && (
                      <Star size={16} style={{
                        color: getColor('warning'),
                      }} />
                    )}
                    {user.username}
                  </h4>
                  <p style={{
                    margin: 0,
                    fontSize: '14px',
                    color: getColor('textLight'),
                  }}>{user.email}</p>
                </div>
              </div>
              
              <div style={{
                marginTop: 'auto',
                display: 'flex',
                justifyContent: 'flex-end',
              }}>
                <button
                  onClick={() => handleQuickAssignUser(user)}
                  className="user-assign-button"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '12px 20px',
                    backgroundColor: isDarkMode ? 'rgba(99, 102, 241, 0.15)' : '#e0e7ff',
                    color: getColor('primary'),
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: isDarkMode ? 'rgba(99, 102, 241, 0.25)' : '#c7d2fe',
                      transform: 'translateY(-2px)',
                    },
                    '&:active': {
                      transform: 'translateY(0)',
                    },
                  }}
                >
                  <Home size={16} />
                  <span>Zimmer zuweisen</span>
                </button>
              </div>
            </div>
          ))}
          
          {getUnassignedUsers().length === 0 && (
            <div style={{
              flex: '1 1 100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '60px 0',
              gap: '20px',
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '80px',
                height: '80px',
                borderRadius: '40px',
                backgroundColor: isDarkMode ? 'rgba(99, 102, 241, 0.1)' : '#e0e7ff',
              }}>
                <Users size={36} style={{
                  color: getColor('primary'),
                  opacity: 0.8,
                }} />
              </div>
              <p style={{
                fontSize: '17px',
                color: getColor('textMedium'),
                textAlign: 'center',
                maxWidth: '400px',
                lineHeight: '1.6',
              }}>
                Alle Lernende haben bereits ein Zimmer zugewiesen.
              </p>
            </div>
          )}
        </div>
      ) : (
        <div style={{
          overflowX: 'auto',
          borderRadius: '16px',
          border: `1px solid ${getColor('border')}`,
          boxShadow: `0 4px 15px rgba(0, 0, 0, ${isDarkMode ? '0.15' : '0.05'})`,
        }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '14px',
          }}>
            <thead>
              <tr>
                <th style={{
                  textAlign: 'left',
                  padding: '16px 20px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: getColor('textMedium'),
                  borderBottom: `1px solid ${getColor('border')}`,
                  backgroundColor: isDarkMode ? COLORS.dark.surfaceAlt : '#f8fafc',
                  position: 'sticky',
                  top: 0,
                }}>Name</th>
                <th style={{
                  textAlign: 'left',
                  padding: '16px 20px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: getColor('textMedium'),
                  borderBottom: `1px solid ${getColor('border')}`,
                  backgroundColor: isDarkMode ? COLORS.dark.surfaceAlt : '#f8fafc',
                  position: 'sticky',
                  top: 0,
                }}>E-Mail</th>
                <th style={{
                  textAlign: 'left',
                  padding: '16px 20px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: getColor('textMedium'),
                  borderBottom: `1px solid ${getColor('border')}`,
                  backgroundColor: isDarkMode ? COLORS.dark.surfaceAlt : '#f8fafc',
                  position: 'sticky',
                  top: 0,
                }}>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {getSortedUnassignedUsers().map((user) => (
                <tr 
                  key={user.id} 
                  style={{
                    borderBottom: `1px solid ${getColor('border')}`,
                    backgroundColor: preferredUsers.includes(user.email) 
                      ? isDarkMode ? 'rgba(99, 102, 241, 0.08)' : '#f5f3ff'
                      : 'transparent',
                    transition: 'background-color 0.2s ease',
                    '&:hover': {
                      backgroundColor: isDarkMode ? COLORS.dark.surfaceHover : '#f8fafc',
                    },
                  }}
                >
                  <td style={{
                    padding: '16px 20px',
                    fontSize: '14px',
                    color: getColor('text'),
                    verticalAlign: 'middle',
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                    }}>
                      <div style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '18px',
                        overflow: 'hidden',
                        backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#f1f5f9',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        {user.profileImagePath ? (
                          <img 
                            src={user.profileImagePath.startsWith('http') ? user.profileImagePath : `${BASE_URL}${user.profileImagePath}`} 
                            alt={user.username} 
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }} 
                            onError={(e) => { e.target.src = '/api/placeholder/72/72'; }}
                          />
                        ) : (
                          <div style={{
                            fontSize: '14px',
                            fontWeight: '600',
                            color: getColor('textLight'),
                          }}>
                            {user.username.split(' ').map(name => name[0]).join('')}
                          </div>
                        )}
                      </div>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                      }}>
                        {preferredUsers.includes(user.email) && (
                          <Star size={14} style={{
                            color: getColor('warning'),
                          }} />
                        )}
                        <span>{user.username}</span>
                      </div>
                    </div>
                  </td>
                  <td style={{
                    padding: '16px 20px',
                    fontSize: '14px',
                    color: getColor('textMedium'),
                    verticalAlign: 'middle',
                  }}>{user.email}</td>
                  <td style={{
                    padding: '16px 20px',
                    fontSize: '14px',
                    color: getColor('text'),
                    verticalAlign: 'middle',
                  }}>
                    <button
                      onClick={() => handleQuickAssignUser(user)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '10px 16px',
                        backgroundColor: isDarkMode ? 'rgba(99, 102, 241, 0.15)' : '#e0e7ff',
                        color: getColor('primary'),
                        border: 'none',
                        borderRadius: '10px',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: isDarkMode ? 'rgba(99, 102, 241, 0.25)' : '#c7d2fe',
                        },
                      }}
                    >
                      <Home size={14} />
                      <span>Zimmer zuweisen</span>
                    </button>
                  </td>
                </tr>
              ))}
              
              {getUnassignedUsers().length === 0 && (
                <tr>
                  <td colSpan={3} style={{
                    padding: '40px 20px',
                    textAlign: 'center',
                    color: getColor('textLight'),
                  }}>
                    Alle Lernende haben bereits ein Zimmer zugewiesen.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderAssignmentsSection = () => (
    <div style={{
      marginBottom: '40px',
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px',
      }}>
        <h3 style={{
          fontSize: '22px',
          fontWeight: '600',
          color: getColor('text'),
          margin: 0,
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '10px',
            borderRadius: '12px',
            backgroundColor: isDarkMode ? 'rgba(6, 182, 212, 0.1)' : '#ecfeff',
            color: getColor('accent'),
          }}>
            <BarChart size={20} />
          </div>
          Zimmerliste
        </h3>
        <div style={{
          display: 'flex',
          gap: '12px',
        }}>
          <button
            onClick={toggleViewMode}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '10px',
              backgroundColor: 'transparent',
              color: getColor('textLight'),
              border: `1px solid ${getColor('border')}`,
              borderRadius: '12px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                color: getColor('text'),
                borderColor: getColor('borderAlt'),
              },
            }}
            title={viewMode === 'cards' ? 'Tabelle anzeigen' : 'Karten anzeigen'}
          >
            {viewMode === 'cards' ? <AlignJustify size={18} /> : <LayoutGrid size={18} />}
          </button>
          <button 
            onClick={toggleExportPanel}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 18px',
              backgroundColor: getColor('accent'),
              color: '#ffffff',
              border: 'none',
              borderRadius: '12px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              boxShadow: `0 4px 12px rgba(6, 182, 212, ${isDarkMode ? '0.3' : '0.2'})`,
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: isDarkMode ? '#0891b2' : '#0891b2',
                transform: 'translateY(-2px)',
                boxShadow: `0 6px 15px rgba(6, 182, 212, ${isDarkMode ? '0.4' : '0.25'})`,
              },
              '&:active': {
                transform: 'translateY(0)',
              },
            }}
          >
            <Download size={16} />
            <span>Exportieren</span>
          </button>
        </div>
      </div>

      {showExportPanel && (
        <div className="export-panel" style={{
          backgroundColor: isDarkMode ? COLORS.dark.surfaceAlt : '#ffffff',
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '24px',
          boxShadow: `0 4px 20px rgba(0, 0, 0, ${isDarkMode ? '0.25' : '0.1'})`,
          animation: 'fadeIn 0.3s ease',
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px',
            borderBottom: `1px solid ${getColor('border')}`,
            paddingBottom: '14px',
          }}>
            <h4 style={{
             margin: 0,
             fontSize: '18px',
             fontWeight: '600',
             color: getColor('text'),
           }}>Liste exportieren</h4>
           <button onClick={toggleExportPanel} style={{
             display: 'flex',
             alignItems: 'center',
             justifyContent: 'center',
             padding: '8px',
             backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f1f5f9',
             border: 'none',
             borderRadius: '10px',
             cursor: 'pointer',
             color: getColor('textMedium'),
             transition: 'all 0.2s ease',
             '&:hover': {
               backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0',
               color: getColor('text'),
             },
           }}>
             <X size={18} />
           </button>
         </div>
         <div style={{
           display: 'flex',
           flexDirection: 'column',
           gap: '20px',
         }}>
           <div style={{
             display: 'flex',
             flexWrap: 'wrap',
             gap: '14px',
             alignItems: 'center',
           }}>
             <div style={{
               display: 'flex',
               alignItems: 'center',
               gap: '12px',
               minWidth: '120px',
             }}>
               <span style={{
                 fontSize: '15px',
                 fontWeight: '500',
                 color: getColor('textMedium'),
               }}>Format:</span>
             </div>
             <div style={{
               display: 'flex',
               gap: '8px',
             }}>
               <button 
                 onClick={() => setExportFormat('csv')} 
                 style={{
                   padding: '10px 16px',
                   backgroundColor: exportFormat === 'csv' 
                     ? isDarkMode ? 'rgba(6, 182, 212, 0.2)' : '#cffafe'
                     : isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f1f5f9',
                   border: exportFormat === 'csv'
                     ? `1px solid ${isDarkMode ? 'rgba(6, 182, 212, 0.4)' : '#22d3ee'}`
                     : `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0'}`,
                   borderRadius: '10px',
                   fontSize: '14px',
                   fontWeight: exportFormat === 'csv' ? '500' : 'normal',
                   color: exportFormat === 'csv'
                     ? isDarkMode ? COLORS.dark.accent : COLORS.light.accent
                     : getColor('textMedium'),
                   cursor: 'pointer',
                   transition: 'all 0.2s ease',
                   '&:hover': {
                     backgroundColor: exportFormat !== 'csv'
                       ? isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0'
                       : isDarkMode ? 'rgba(6, 182, 212, 0.25)' : '#a5f3fc',
                   },
                 }}
               >
                 CSV
               </button>
               <button 
                 onClick={() => setExportFormat('text')} 
                 style={{
                   padding: '10px 16px',
                   backgroundColor: exportFormat === 'text' 
                     ? isDarkMode ? 'rgba(6, 182, 212, 0.2)' : '#cffafe'
                     : isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f1f5f9',
                   border: exportFormat === 'text'
                     ? `1px solid ${isDarkMode ? 'rgba(6, 182, 212, 0.4)' : '#22d3ee'}`
                     : `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0'}`,
                   borderRadius: '10px',
                   fontSize: '14px',
                   fontWeight: exportFormat === 'text' ? '500' : 'normal',
                   color: exportFormat === 'text'
                     ? isDarkMode ? COLORS.dark.accent : COLORS.light.accent
                     : getColor('textMedium'),
                   cursor: 'pointer',
                   transition: 'all 0.2s ease',
                   '&:hover': {
                     backgroundColor: exportFormat !== 'text'
                       ? isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0'
                       : isDarkMode ? 'rgba(6, 182, 212, 0.25)' : '#a5f3fc',
                   },
                 }}
               >
                 Text
               </button>
               <button 
                 onClick={() => setExportFormat('list')} 
                 style={{
                   padding: '10px 16px',
                   backgroundColor: exportFormat === 'list' 
                     ? isDarkMode ? 'rgba(6, 182, 212, 0.2)' : '#cffafe'
                     : isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f1f5f9',
                   border: exportFormat === 'list'
                     ? `1px solid ${isDarkMode ? 'rgba(6, 182, 212, 0.4)' : '#22d3ee'}`
                     : `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0'}`,
                   borderRadius: '10px',
                   fontSize: '14px',
                   fontWeight: exportFormat === 'list' ? '500' : 'normal',
                   color: exportFormat === 'list'
                     ? isDarkMode ? COLORS.dark.accent : COLORS.light.accent
                     : getColor('textMedium'),
                   cursor: 'pointer',
                   transition: 'all 0.2s ease',
                   '&:hover': {
                     backgroundColor: exportFormat !== 'list'
                       ? isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0'
                       : isDarkMode ? 'rgba(6, 182, 212, 0.25)' : '#a5f3fc',
                   },
                 }}
               >
                 Tabelliert
               </button>
             </div>
           </div>
           
           <div style={{
             display: 'flex',
             flexDirection: 'column',
             gap: '10px',
           }}>
             <span style={{
               fontSize: '15px',
               fontWeight: '500',
               color: getColor('textMedium'),
             }}>Vorschau:</span>
             <pre ref={exportTextRef} style={{
               backgroundColor: isDarkMode ? 'rgba(15, 23, 42, 0.5)' : '#f8fafc',
               padding: '16px',
               borderRadius: '12px',
               fontFamily: 'monospace',
               fontSize: '13px',
               lineHeight: '1.6',
               whiteSpace: 'pre-wrap',
               maxHeight: '240px',
               overflowY: 'auto',
               color: getColor('textMedium'),
               border: `1px solid ${getColor('border')}`,
             }}>
               {generateExportData()}
             </pre>
           </div>
           
           <div style={{
             display: 'flex',
             justifyContent: 'flex-end',
             alignItems: 'center',
             gap: '16px',
             marginTop: '10px',
           }}>
             {copySuccess && (
               <div style={{
                 display: 'flex',
                 alignItems: 'center',
                 gap: '8px',
                 fontSize: '14px',
                 color: getColor('success'),
               }}>
                 <CheckCircle size={16} />
                 <span>{copySuccess}</span>
               </div>
             )}
             <button 
               onClick={copyToClipboard} 
               style={{
                 display: 'flex',
                 alignItems: 'center',
                 gap: '8px',
                 padding: '12px 18px',
                 backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f1f5f9',
                 color: getColor('textMedium'),
                 border: `1px solid ${getColor('border')}`,
                 borderRadius: '12px',
                 fontSize: '14px',
                 fontWeight: '500',
                 cursor: 'pointer',
                 transition: 'all 0.2s ease',
                 '&:hover': {
                   backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0',
                   color: getColor('text'),
                   transform: 'translateY(-2px)',
                 },
                 '&:active': {
                   transform: 'translateY(0)',
                 },
               }}
             >
               <Copy size={16} />
               <span>Kopieren</span>
             </button>
             <button 
               onClick={downloadExportData} 
               style={{
                 display: 'flex',
                 alignItems: 'center',
                 gap: '8px',
                 padding: '12px 20px',
                 backgroundColor: getColor('accent'),
                 color: '#ffffff',
                 border: 'none',
                 borderRadius: '12px',
                 fontSize: '14px',
                 fontWeight: '500',
                 cursor: 'pointer',
                 boxShadow: `0 4px 12px rgba(6, 182, 212, ${isDarkMode ? '0.3' : '0.2'})`,
                 transition: 'all 0.3s ease',
                 '&:hover': {
                   backgroundColor: isDarkMode ? '#0891b2' : '#0891b2',
                   transform: 'translateY(-2px)',
                   boxShadow: `0 6px 15px rgba(6, 182, 212, ${isDarkMode ? '0.4' : '0.25'})`,
                 },
                 '&:active': {
                   transform: 'translateY(0)',
                 },
               }}
             >
               <Download size={16} />
               <span>Herunterladen</span>
             </button>
           </div>
         </div>
       </div>
     )}

     {isLoading ? (
       <div style={{
         display: 'flex',
         flexDirection: 'column',
         alignItems: 'center',
         justifyContent: 'center',
         padding: '60px 0',
         gap: '20px',
       }}>
         <div className="loading-pulse" style={{
           display: 'flex',
           alignItems: 'center',
           justifyContent: 'center',
           color: getColor('primary'),
           animation: 'pulse 1.5s ease-in-out infinite',
         }}>
           <Loader size={32} style={{
             animation: 'spin 1s linear infinite',
           }} />
         </div>
         <span style={{
           fontSize: '17px',
           color: getColor('textMedium'),
         }}>Laden...</span>
       </div>
     ) : filteredAssignments.length === 0 ? (
       <div style={{
         display: 'flex',
         flexDirection: 'column',
         alignItems: 'center',
         justifyContent: 'center',
         padding: '60px 0',
         gap: '20px',
       }}>
         <div style={{
           display: 'flex',
           alignItems: 'center',
           justifyContent: 'center',
           width: '80px',
           height: '80px',
           borderRadius: '40px',
           backgroundColor: isDarkMode ? 'rgba(6, 182, 212, 0.1)' : '#ecfeff',
         }}>
           <Home size={36} style={{
             color: getColor('accent'),
             opacity: 0.8,
           }} />
         </div>
         <p style={{
           fontSize: '17px',
           color: getColor('textMedium'),
           textAlign: 'center',
           maxWidth: '400px',
           lineHeight: '1.6',
         }}>
           {searchText ? 'Keine Ergebnisse gefunden.' : 'Keine Zimmerzuweisungen vorhanden.'}
         </p>
       </div>
     ) : viewMode === 'cards' ? (
       <div style={{
         display: 'flex',
         flexWrap: 'wrap',
         gap: '20px',
       }}>
         {filteredAssignments.map(assignment => (
           <div 
             key={assignment.userId} 
             className="room-card"
             style={{
               flex: '1 1 280px',
               maxWidth: '340px',
               backgroundColor: isDarkMode ? COLORS.dark.surfaceAlt : '#ffffff',
               borderRadius: '16px',
               padding: '24px',
               boxShadow: `0 4px 15px rgba(0, 0, 0, ${isDarkMode ? '0.15' : '0.05'})`,
               border: `1px solid ${isDarkMode ? 'transparent' : getColor('border')}`,
               display: 'flex',
               flexDirection: 'column',
               gap: '16px',
               transition: 'all 0.3s ease',
               ...(preferredUsers.includes(assignment.email) 
                 ? {
                     borderLeft: `4px solid ${getColor('accent')}`,
                     backgroundColor: isDarkMode ? 'rgba(6, 182, 212, 0.08)' : '#ecfeff',
                   } 
                 : {}),
               '&:hover': {
                 transform: 'translateY(-4px)',
                 boxShadow: `0 10px 20px rgba(0, 0, 0, ${isDarkMode ? '0.25' : '0.1'})`,
               },
             }}
           >
             <div style={{
               display: 'flex',
               justifyContent: 'space-between',
               alignItems: 'flex-start',
             }}>
               <div style={{
                 display: 'flex',
                 flexDirection: 'column',
                 gap: '4px',
               }}>
                 <div style={{
                   display: 'inline-flex',
                   alignItems: 'center',
                   justifyContent: 'center',
                   minWidth: '48px',
                   height: '48px',
                   padding: '0 14px',
                   borderRadius: '12px',
                   backgroundColor: isDarkMode ? 'rgba(6, 182, 212, 0.15)' : '#cffafe',
                   color: getColor('accent'),
                   fontWeight: '700',
                   fontSize: '18px',
                 }}>
                   {assignment.roomNumber}
                 </div>
                 {assignment.building && (
                   <div style={{
                     display: 'flex',
                     alignItems: 'center',
                     gap: '6px',
                     marginTop: '4px',
                     fontSize: '13px',
                     color: getColor('textLight'),
                   }}>
                     <Building size={14} />
                     <span>{assignment.building}</span>
                   </div>
                 )}
               </div>
               <div style={{
                 display: 'flex',
                 gap: '6px',
               }}>
                 <button
                   onClick={() => openEditForm(assignment)}
                   style={{
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'center',
                     width: '34px',
                     height: '34px',
                     backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f1f5f9',
                     border: 'none',
                     borderRadius: '8px',
                     color: getColor('textMedium'),
                     cursor: 'pointer',
                     transition: 'all 0.2s ease',
                     '&:hover': {
                       backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0',
                       color: getColor('text'),
                       transform: 'translateY(-2px)',
                     },
                   }}
                   title="Bearbeiten"
                 >
                   <Edit size={16} />
                 </button>
                 <button
                   onClick={() => handleDeleteAssignment(assignment)}
                   style={{
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'center',
                     width: '34px',
                     height: '34px',
                     backgroundColor: isDarkMode ? 'rgba(239, 68, 68, 0.1)' : '#fee2e2',
                     border: 'none',
                     borderRadius: '8px',
                     color: getColor('error'),
                     cursor: 'pointer',
                     transition: 'all 0.2s ease',
                     '&:hover': {
                       backgroundColor: isDarkMode ? 'rgba(239, 68, 68, 0.2)' : '#fecaca',
                       transform: 'translateY(-2px)',
                     },
                   }}
                   title="Löschen"
                 >
                   <Trash size={16} />
                 </button>
               </div>
             </div>
             
             <div style={{
               display: 'flex',
               alignItems: 'center',
               gap: '14px',
               marginTop: '12px',
             }}>
               <div style={{
                 width: '44px',
                 height: '44px',
                 borderRadius: '22px',
                 overflow: 'hidden',
                 backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#f1f5f9',
                 display: 'flex',
                 alignItems: 'center',
                 justifyContent: 'center',
                 boxShadow: `0 2px 6px rgba(0, 0, 0, ${isDarkMode ? '0.2' : '0.05'})`,
               }}>
                 {assignment.profileImagePath ? (
                   <img 
                     src={assignment.profileImagePath.startsWith('http') ? assignment.profileImagePath : `${BASE_URL}${assignment.profileImagePath}`} 
                     alt={assignment.username} 
                     style={{
                       width: '100%',
                       height: '100%',
                       objectFit: 'cover',
                     }} 
                     onError={(e) => { e.target.src = '/api/placeholder/88/88'; }}
                   />
                 ) : (
                   <div style={{
                     fontSize: '16px',
                     fontWeight: '600',
                     color: getColor('textLight'),
                   }}>
                     {assignment.username.split(' ').map(name => name[0]).join('')}
                   </div>
                 )}
               </div>
               <div style={{
                 flex: 1,
               }}>
                 <div style={{
                   display: 'flex',
                   alignItems: 'center',
                   gap: '6px',
                   fontSize: '16px',
                   fontWeight: '600',
                   color: getColor('text'),
                 }}>
                   {preferredUsers.includes(assignment.email) && (
                     <Star size={15} style={{
                       color: getColor('warning'),
                     }} />
                   )}
                   <span>{assignment.username}</span>
                 </div>
                 <div style={{
                   fontSize: '13px',
                   color: getColor('textLight'),
                   marginTop: '2px',
                 }}>
                   {assignment.email}
                 </div>
               </div>
             </div>
             
             {assignment.assignmentDate && (
               <div style={{
                 marginTop: '8px',
                 fontSize: '13px',
                 color: getColor('textLight'),
                 display: 'flex',
                 alignItems: 'center',
                 gap: '6px',
               }}>
                 <span>Letztes Update:</span>
                 <span>{new Date(assignment.assignmentDate).toLocaleString('de-DE')}</span>
               </div>
             )}
           </div>
         ))}
       </div>
     ) : (
       <div style={{
         overflowX: 'auto',
         borderRadius: '16px',
         border: `1px solid ${getColor('border')}`,
         boxShadow: `0 4px 15px rgba(0, 0, 0, ${isDarkMode ? '0.15' : '0.05'})`,
       }}>
         <table style={{
           width: '100%',
           borderCollapse: 'collapse',
           fontSize: '14px',
         }}>
           <thead>
             <tr>
               <th style={{
                 textAlign: 'left',
                 padding: '16px 20px',
                 fontSize: '14px',
                 fontWeight: '600',
                 color: getColor('textMedium'),
                 borderBottom: `1px solid ${getColor('border')}`,
                 backgroundColor: isDarkMode ? COLORS.dark.surfaceAlt : '#f8fafc',
                 position: 'sticky',
                 top: 0,
               }}>Zimmer</th>
               <th style={{
                 textAlign: 'left',
                 padding: '16px 20px',
                 fontSize: '14px',
                 fontWeight: '600',
                 color: getColor('textMedium'),
                 borderBottom: `1px solid ${getColor('border')}`,
                 backgroundColor: isDarkMode ? COLORS.dark.surfaceAlt : '#f8fafc',
                 position: 'sticky',
                 top: 0,
               }}>Gebäude</th>
               <th style={{
                 textAlign: 'left',
                 padding: '16px 20px',
                 fontSize: '14px',
                 fontWeight: '600',
                 color: getColor('textMedium'),
                 borderBottom: `1px solid ${getColor('border')}`,
                 backgroundColor: isDarkMode ? COLORS.dark.surfaceAlt : '#f8fafc',
                 position: 'sticky',
                 top: 0,
               }}>Lernende</th>
               <th style={{
                 textAlign: 'left',
                 padding: '16px 20px',
                 fontSize: '14px',
                 fontWeight: '600',
                 color: getColor('textMedium'),
                 borderBottom: `1px solid ${getColor('border')}`,
                 backgroundColor: isDarkMode ? COLORS.dark.surfaceAlt : '#f8fafc',
                 position: 'sticky',
                 top: 0,
               }}>E-Mail</th>
               <th style={{
                 textAlign: 'left',
                 padding: '16px 20px',
                 fontSize: '14px',
                 fontWeight: '600',
                 color: getColor('textMedium'),
                 borderBottom: `1px solid ${getColor('border')}`,
                 backgroundColor: isDarkMode ? COLORS.dark.surfaceAlt : '#f8fafc',
                 position: 'sticky',
                 top: 0,
               }}>Letztes Update</th>
               <th style={{
                 textAlign: 'left',
                 padding: '16px 20px',
                 fontSize: '14px',
                 fontWeight: '600',
                 color: getColor('textMedium'),
                 borderBottom: `1px solid ${getColor('border')}`,
                 backgroundColor: isDarkMode ? COLORS.dark.surfaceAlt : '#f8fafc',
                 position: 'sticky',
                 top: 0,
               }}>Aktionen</th>
             </tr>
           </thead>
           <tbody>
             {filteredAssignments.map((assignment) => (
               <tr 
                 key={assignment.userId} 
                 style={{
                   borderBottom: `1px solid ${getColor('border')}`,
                   backgroundColor: preferredUsers.includes(assignment.email) 
                     ? isDarkMode ? 'rgba(6, 182, 212, 0.08)' : '#ecfeff'
                     : 'transparent',
                   transition: 'background-color 0.2s ease',
                   '&:hover': {
                     backgroundColor: isDarkMode ? COLORS.dark.surfaceHover : '#f8fafc',
                   },
                 }}
               >
                 <td style={{
                   padding: '16px 20px',
                   fontSize: '14px',
                   color: getColor('text'),
                   verticalAlign: 'middle',
                 }}>
                   <div style={{
                     display: 'inline-flex',
                     alignItems: 'center',
                     justifyContent: 'center',
                     minWidth: '36px',
                     height: '36px',
                     padding: '0 12px',
                     borderRadius: '10px',
                     backgroundColor: isDarkMode ? 'rgba(6, 182, 212, 0.15)' : '#cffafe',
                     color: getColor('accent'),
                     fontWeight: '600',
                     fontSize: '15px',
                   }}>
                     {assignment.roomNumber}
                   </div>
                 </td>
                 <td style={{
                   padding: '16px 20px',
                   fontSize: '14px',
                   color: getColor('textMedium'),
                   verticalAlign: 'middle',
                 }}>
                   {assignment.building ? (
                     <div style={{
                       display: 'inline-flex',
                       alignItems: 'center',
                       gap: '6px',
                       padding: '6px 12px',
                       backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f1f5f9',
                       borderRadius: '8px',
                       fontSize: '13px',
                       color: getColor('textMedium'),
                     }}>
                       <Building size={14} />
                       <span>{assignment.building}</span>
                     </div>
                   ) : (
                     <span style={{
                       color: getColor('textLight'),
                       fontStyle: 'italic',
                     }}>-</span>
                   )}
                 </td>
                 <td style={{
                   padding: '16px 20px',
                   fontSize: '14px',
                   color: getColor('text'),
                   verticalAlign: 'middle',
                 }}>
                   <div style={{
                     display: 'flex',
                     alignItems: 'center',
                     gap: '10px',
                   }}>
                     <div style={{
                       width: '32px',
                       height: '32px',
                       borderRadius: '16px',
                       overflow: 'hidden',
                       backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#f1f5f9',
                       display: 'flex',
                       alignItems: 'center',
                       justifyContent: 'center',
                     }}>
                       {assignment.profileImagePath ? (
                         <img 
                           src={assignment.profileImagePath.startsWith('http') ? assignment.profileImagePath : `${BASE_URL}${assignment.profileImagePath}`} 
                           alt={assignment.username} 
                           style={{
                             width: '100%',
                             height: '100%',
                             objectFit: 'cover',
                           }} 
                           onError={(e) => { e.target.src = '/api/placeholder/64/64'; }}
                         />
                       ) : (
                         <div style={{
                           fontSize: '14px',
                           fontWeight: '600',
                           color: getColor('textLight'),
                         }}>
                           {assignment.username.split(' ').map(name => name[0]).join('')}
                         </div>
                       )}
                     </div>
                     <div style={{
                       display: 'flex',
                       alignItems: 'center',
                       gap: '6px',
                     }}>
                       {preferredUsers.includes(assignment.email) && (
                         <Star size={14} style={{
                           color: getColor('warning'),
                         }} />
                       )}
                       <span>{assignment.username}</span>
                     </div>
                   </div>
                 </td>
                 <td style={{
                   padding: '16px 20px',
                   fontSize: '14px',
                   color: getColor('textMedium'),
                   verticalAlign: 'middle',
                 }}>{assignment.email}</td>
                 <td style={{
                   padding: '16px 20px',
                   fontSize: '14px',
                   color: getColor('textMedium'),
                   verticalAlign: 'middle',
                 }}>
                   {assignment.assignmentDate 
                     ? new Date(assignment.assignmentDate).toLocaleString('de-DE')
                     : '-'}
                 </td>
                 <td style={{
                   padding: '16px 20px',
                   fontSize: '14px',
                   color: getColor('text'),
                   verticalAlign: 'middle',
                 }}>
                   <div style={{
                     display: 'flex',
                     gap: '8px',
                   }}>
                     <button 
                       onClick={() => openEditForm(assignment)}
                       style={{
                         display: 'flex',
                         alignItems: 'center',
                         justifyContent: 'center',
                         width: '34px',
                         height: '34px',
                         backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f1f5f9',
                         border: `1px solid ${getColor('border')}`,
                         borderRadius: '8px',
                         color: getColor('textMedium'),
                         cursor: 'pointer',
                         transition: 'all 0.2s ease',
                         '&:hover': {
                           backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0',
                           color: getColor('text'),
                           transform: 'translateY(-2px)',
                         },
                       }}
                       title="Bearbeiten"
                     >
                       <Edit size={16} />
                     </button>
                     <button 
                       onClick={() => handleDeleteAssignment(assignment)}
                       style={{
                         display: 'flex',
                         alignItems: 'center',
                         justifyContent: 'center',
                         width: '34px',
                         height: '34px',
                         backgroundColor: isDarkMode ? 'rgba(239, 68, 68, 0.1)' : '#fee2e2',
                         border: `1px solid ${isDarkMode ? 'rgba(239, 68, 68, 0.2)' : '#fecaca'}`,
                         borderRadius: '8px',
                         color: getColor('error'),
                         cursor: 'pointer',
                         transition: 'all 0.2s ease',
                         '&:hover': {
                           backgroundColor: isDarkMode ? 'rgba(239, 68, 68, 0.2)' : '#fecaca',
                           transform: 'translateY(-2px)',
                         },
                       }}
                       title="Löschen"
                     >
                       <Trash size={16} />
                     </button>
                   </div>
                 </td>
               </tr>
             ))}
           </tbody>
         </table>
       </div>
     )}
   </div>
 );

 const renderAssignmentForm = () => {
   if (!showManualForm) return null;
   
   return (
     <div className="modal-overlay" style={{
       position: 'fixed',
       top: '0',
       left: '0',
       right: '0',
       bottom: '0',
       backgroundColor: 'rgba(0, 0, 0, 0.6)',
       backdropFilter: 'blur(8px)',
       display: 'flex',
       alignItems: 'center',
       justifyContent: 'center',
       zIndex: '100',
       animation: 'fadeIn 0.3s ease-out',
     }}>
       <div 
         className="modal-content"
         style={{
           width: '100%',
           maxWidth: '560px',
           backgroundColor: isDarkMode ? COLORS.dark.surfaceAlt : '#ffffff',
           borderRadius: '20px',
           boxShadow: `0 20px 60px rgba(0, 0, 0, ${isDarkMode ? '0.4' : '0.15'})`,
           padding: '28px',
           maxHeight: '90vh',
           overflowY: 'auto',
           animation: 'slideInUp 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
         }}
       >
         <div style={{
           display: 'flex',
           justifyContent: 'space-between',
           alignItems: 'center',
           marginBottom: '24px',
           borderBottom: `1px solid ${getColor('border')}`,
           paddingBottom: '20px',
         }}>
           <h4 style={{
             fontSize: '20px',
             fontWeight: '600',
             color: getColor('text'),
             margin: '0',
             display: 'flex',
             alignItems: 'center',
             gap: '10px',
           }}>
             <div style={{
               display: 'flex',
               alignItems: 'center',
               justifyContent: 'center',
               width: '32px',
               height: '32px',
               borderRadius: '16px',
               backgroundColor: formMode === 'add' 
                 ? isDarkMode ? 'rgba(16, 185, 129, 0.1)' : '#ecfdf5'
                 : isDarkMode ? 'rgba(99, 102, 241, 0.1)' : '#eef2ff',
               color: formMode === 'add' ? getColor('success') : getColor('primary'),
             }}>
               {formMode === 'add' ? <Plus size={18} /> : <Edit size={18} />}
             </div>
             {formMode === 'add' ? 'Neue Zimmerzuweisung' : 'Zimmerzuweisung bearbeiten'}
           </h4>
           <button 
             onClick={() => setShowManualForm(false)} 
             style={{
               display: 'flex',
               alignItems: 'center',
               justifyContent: 'center',
               width: '36px',
               height: '36px',
               backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f1f5f9',
               border: 'none',
               borderRadius: '18px',
               color: getColor('textMedium'),
               cursor: 'pointer',
               transition: 'all 0.2s ease',
               '&:hover': {
                 backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0',
                 color: getColor('text'),
               },
             }}
           >
             <X size={20} />
           </button>
         </div>
         
         <form onSubmit={handleSubmitManualForm} style={{
           display: 'flex',
           flexDirection: 'column',
           gap: '24px',
         }}>
           <div style={{
             display: 'flex',
             flexDirection: 'column',
             gap: '10px',
           }}>
             <label style={{
               fontSize: '15px',
               fontWeight: '500',
               color: getColor('textMedium'),
             }}>Lernende</label>
             {formMode === 'add' ? (
               <>
                 <select
                   name="email"
                   value={manualFormData.email}
                   onChange={handleFormInputChange}
                   style={{
                     padding: '14px 16px',
                     borderRadius: '12px',
                     border: `1px solid ${formErrors.email 
                       ? getColor('error') 
                       : getColor('border')}`,
                     fontSize: '15px',
                     outline: 'none',
                     backgroundColor: isDarkMode 
                       ? formErrors.email 
                         ? 'rgba(239, 68, 68, 0.1)' 
                         : 'rgba(255, 255, 255, 0.05)'
                       : formErrors.email 
                         ? '#fee2e2' 
                         : '#f8fafc',
                     color: getColor('text'),
                     transition: 'all 0.2s ease',
                     appearance: 'none',
                     backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='${
                       isDarkMode ? encodeURIComponent('#94a3b8') : encodeURIComponent('#64748b')
                     }' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
                     backgroundRepeat: 'no-repeat',
                     backgroundPosition: 'right 16px center',
                     backgroundSize: '16px',
                     '&:focus': {
                       borderColor: getColor('primary'),
                       boxShadow: `0 0 0 3px ${isDarkMode 
                         ? 'rgba(99, 102, 241, 0.2)' 
                         : 'rgba(99, 102, 241, 0.1)'}`,
                     },
                   }}
                 >
                   <option value="">-- Lernende auswählen --</option>
                   {getSortedUnassignedUsers().map(user => (
                     <option key={user.id} value={user.email}>
                       {user.username} ({user.email})
                       {preferredUsers.includes(user.email) ? ' ⭐' : ''}
                     </option>
                   ))}
                 </select>
                 {formErrors.email && (
                   <div style={{
                     fontSize: '13px',
                     color: getColor('error'),
                     marginTop: '4px',
                     display: 'flex',
                     alignItems: 'center',
                     gap: '6px',
                   }}>
                     <AlertCircle size={14} />
                     {formErrors.email}
                   </div>
                 )}
               </>
             ) : (
               <div style={{
                 padding: '14px 16px',
                 borderRadius: '12px',
                 backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc',
                 fontSize: '15px',
                 color: getColor('text'),
                 border: `1px solid ${getColor('border')}`,
                 display: 'flex',
                 alignItems: 'center',
                 gap: '10px',
               }}>
                 {findUserByEmail(manualFormData.email)?.profileImagePath ? (
                   <img 
                     src={findUserByEmail(manualFormData.email)?.profileImagePath.startsWith('http') 
                       ? findUserByEmail(manualFormData.email)?.profileImagePath 
                       : `${BASE_URL}${findUserByEmail(manualFormData.email)?.profileImagePath}`
                     } 
                     alt={findUserByEmail(manualFormData.email)?.username || 'Benutzer'} 
                     style={{
                       width: '28px',
                       height: '28px',
                       borderRadius: '14px',
                       objectFit: 'cover',
                     }} 
                     onError={(e) => { e.target.src = '/api/placeholder/56/56'; }}
                   />
                 ) : (
                   <div style={{
                     width: '28px',
                     height: '28px',
                     borderRadius: '14px',
                     backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0',
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'center',
                     fontSize: '13px',
                     fontWeight: '600',
                     color: getColor('textLight'),
                   }}>
                     {(findUserByEmail(manualFormData.email)?.username || manualFormData.email)
                       .split(' ')
                       .map(name => name[0])
                       .join('')}
                   </div>
                 )}
                 <span>
                   {findUserByEmail(manualFormData.email)?.username || manualFormData.email}
                 </span>
               </div>
             )}
           </div>
           
           <div style={{
             display: 'flex',
             gap: '20px',
             flexWrap: 'wrap',
           }}>
             <div style={{
               display: 'flex',
               flexDirection: 'column',
               gap: '10px',
               flex: '1 1 220px',
             }}>
               <label style={{
                 fontSize: '15px',
                 fontWeight: '500',
                 color: getColor('textMedium'),
               }}>Zimmernummer</label>
               <input
                 type="text"
                 name="roomNumber"
                 value={manualFormData.roomNumber}
                 onChange={handleFormInputChange}
                 placeholder="z.B. 101"
                 style={{
                   padding: '14px 16px',
                   borderRadius: '12px',
                   border: `1px solid ${formErrors.roomNumber 
                     ? getColor('error') 
                     : getColor('border')}`,
                   fontSize: '15px',
                   outline: 'none',
                   backgroundColor: isDarkMode 
                     ? formErrors.roomNumber 
                       ? 'rgba(239, 68, 68, 0.1)' 
                       : 'rgba(255, 255, 255, 0.05)'
                     : formErrors.roomNumber 
                       ? '#fee2e2' 
                       : '#f8fafc',
                   color: getColor('text'),
                   transition: 'all 0.2s ease',
                   '&:focus': {
                     borderColor: getColor('primary'),
                     boxShadow: `0 0 0 3px ${isDarkMode 
                       ? 'rgba(99, 102, 241, 0.2)' 
                       : 'rgba(99, 102, 241, 0.1)'}`,
                   },
                   '&::placeholder': {
                     color: getColor('textLight'),
                   },
                 }}
               />
               {formErrors.roomNumber && (
                 <div style={{
                   fontSize: '13px',
                   color: getColor('error'),
                   marginTop: '4px',
                   display: 'flex',
                   alignItems: 'center',
                   gap: '6px',
                 }}>
                   <AlertCircle size={14} />
                   {formErrors.roomNumber}
                 </div>
               )}
             </div>
             
             <div style={{
               display: 'flex',
               flexDirection: 'column',
               gap: '10px',
               flex: '1 1 220px',
             }}>
               <label style={{
                 fontSize: '15px',
                 fontWeight: '500',
                 color: getColor('textMedium'),
               }}>Gebäude (optional)</label>
               <input
                 type="text"
                 name="building"
                 value={manualFormData.building}
                 onChange={handleFormInputChange}
                 placeholder="z.B. Haupthaus"
                 style={{
                   padding: '14px 16px',
                   borderRadius: '12px',
                   border: `1px solid ${getColor('border')}`,
                   fontSize: '15px',
                   outline: 'none',
                   backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc',
                   color: getColor('text'),
                   transition: 'all 0.2s ease',
                   '&:focus': {
                     borderColor: getColor('primary'),
                     boxShadow: `0 0 0 3px ${isDarkMode 
                       ? 'rgba(99, 102, 241, 0.2)' 
                       : 'rgba(99, 102, 241, 0.1)'}`,
                   },
                   '&::placeholder': {
                     color: getColor('textLight'),
                   },
                 }}
               />
             </div>
           </div>
           
           <div style={{
             display: 'flex',
             justifyContent: 'flex-end',
             gap: '16px',
             marginTop: '16px',
             borderTop: `1px solid ${getColor('border')}`,
             paddingTop: '24px',
           }}>
             <button 
               type="button" 
               onClick={() => setShowManualForm(false)}
               style={{
                 padding: '12px 20px',
                 backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f1f5f9',
                 color: getColor('textMedium'),
                 border: `1px solid ${getColor('border')}`,
                 borderRadius: '12px',
                 fontSize: '15px',
                 fontWeight: '500',
                 cursor: 'pointer',
                 transition: 'all 0.2s ease',
                 '&:hover': {
                   backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0',
                   color: getColor('text'),
                   transform: 'translateY(-2px)',
                 },
                 '&:active': {
                   transform: 'translateY(0)',
                 },
               }}
             >
               Abbrechen
             </button>
             <button 
               type="submit" 
               style={{
                 padding: '12px 24px',
                 backgroundColor: getColor('primary'),
                 color: '#ffffff',
                 border: 'none',
                 borderRadius: '12px',
                 fontSize: '15px',
                 fontWeight: '500',
                 cursor: 'pointer',
                 boxShadow: `0 4px 12px rgba(99, 102, 241, ${isDarkMode ? '0.3' : '0.2'})`,
                 transition: 'all 0.3s ease',
                 display: 'flex',
                 alignItems: 'center',
                 gap: '8px',
                 '&:hover': {
                   backgroundColor: getColor('primaryDark'),
                   transform: 'translateY(-2px)',
                   boxShadow: `0 6px 16px rgba(99, 102, 241, ${isDarkMode ? '0.4' : '0.3'})`,
                 },
                 '&:active': {
                   transform: 'translateY(0)',
                 },
               }}
             >
               {formMode === 'add' ? (
                 <>
                   <Plus size={18} />
                   <span>Hinzufügen</span>
                 </>
               ) : (
                 <>
                   <CheckCircle size={18} />
                   <span>Speichern</span>
                 </>
               )}
             </button>
           </div>
         </form>
       </div>
     </div>
   );
 };

 // Main render
 return (
   <div style={{
     backgroundColor: isDarkMode ? COLORS.dark.surface : COLORS.light.surface,
     borderRadius: '20px',
     padding: '24px',
     boxShadow: `0 4px 24px rgba(0, 0, 0, ${isDarkMode ? '0.2' : '0.05'})`,
     margin: '20px 0',
     transition: 'all 0.3s ease',
     maxWidth: '3200px',
     overflow: 'hidden',
     color: getColor('text'),
   }}>
     {/* Notification banner */}
     {renderNotificationBanner()}

     {/* Header */}
     <div 
       ref={headerRef}
       style={{
         position: 'sticky',
         top: '0',
         zIndex: '20',
         backgroundColor: isDarkMode ? COLORS.dark.surface : COLORS.light.surface,
         padding: '16px',
         marginLeft: '-24px',
         marginRight: '-24px',
         marginTop: '-24px',
         marginBottom: '24px',
         transition: 'all 0.3s ease',
       }}
     >
       <div style={{
         display: 'flex',
         justifyContent: 'space-between',
         alignItems: 'center',
         marginBottom: '24px',
         flexWrap: 'wrap',
         gap: '16px',
       }}>
         <div style={{
           display: 'flex',
           alignItems: 'center',
           gap: '16px',
         }}>
           <h2 style={{
             fontSize: '28px',
             fontWeight: '700',
             color: getColor('text'),
             margin: 0,
             display: 'flex',
             alignItems: 'center',
             gap: '12px',
           }}>
             <div style={{
               display: 'flex',
               alignItems: 'center',
               justifyContent: 'center',
               padding: '8px',
               borderRadius: '12px',
               backgroundImage: isDarkMode 
                 ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.3), rgba(6, 182, 212, 0.3))'
                 : 'linear-gradient(135deg, #da291c,rgb(255, 255, 255))',
               boxShadow: isDarkMode
                 ? '0 8px 16px rgba(99, 102, 241, 0.2), 0 4px 6px rgba(6, 182, 212, 0.2)'
                 : '0 8px 16px rgba(99, 102, 241, 0.15), 0 4px 6px rgba(6, 182, 212, 0.15)',
               color: '#ffffff',
             }}>
               <Sparkles size={24} />
             </div>
              Zimmerverwaltung
           </h2>
           <div style={{
             display: 'flex',
             alignItems: 'center',
             gap: '12px',
           }}>
             <button
               onClick={toggleDarkMode}
               style={{
                 display: 'flex',
                 alignItems: 'center',
                 justifyContent: 'center',
                 width: '40px',
                 height: '40px',
                 backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f1f5f9',
                 border: 'none',
                 borderRadius: '20px',
                 cursor: 'pointer',
                 color: getColor('textMedium'),
                 transition: 'all 0.2s ease',
                 '&:hover': {
                   backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0',
                   transform: 'translateY(-2px)',
                 },
               }}
               title={isDarkMode ? 'Light Mode' : 'Dark Mode'}
             >
               {isDarkMode ? '☀️' : '🌙'}
             </button>
             <button
               onClick={() => {
                 // Reset to default settings
                 setActiveTab('upload');
                 setViewMode('cards');
                 setShowManualForm(false);
                 setShowExportPanel(false);
                 setShowAiInfo(false);
                 setShowAiSuggestions(false);
               }}
               style={{
                 display: 'flex',
                 alignItems: 'center',
                 justifyContent: 'center',
                 width: '40px',
                 height: '40px',
                 backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f1f5f9',
                 border: 'none',
                 borderRadius: '20px',
                 cursor: 'pointer',
                 color: getColor('textMedium'),
                 transition: 'all 0.2s ease',
                 '&:hover': {
                   backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0',
                   transform: 'translateY(-2px)',
                 },
               }}
               title="Einstellungen zurücksetzen"
             >
               <Settings size={20} />
             </button>
           </div>
         </div>
         <div style={{
           position: 'relative',
           width: '360px',
           maxWidth: '100%',
         }}>
           <input
             type="text"
             placeholder="Suche nach Lernenden oder Zimmer..."
             value={searchText}
             onChange={handleSearch}
             style={{
               width: '100%',
               padding: '14px 48px',
               borderRadius: '14px',
               border: `1px solid ${getColor('border')}`,
               fontSize: '15px',
               outline: 'none',
               backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc',
               color: getColor('text'),
               transition: 'all 0.2s ease',
               '&:focus': {
                 borderColor: getColor('primary'),
                 boxShadow: `0 0 0 3px ${isDarkMode ? 'rgba(99, 102, 241, 0.2)' : 'rgba(99, 102, 241, 0.1)'}`,
               },
               '&::placeholder': {
                 color: getColor('textLight'),
               },
             }}
           />
           <Search size={20} style={{
             position: 'absolute',
             left: '16px',
             top: '50%',
             transform: 'translateY(-50%)',
             color: getColor('textLight'),
           }} />
           {searchText && (
             <button
               onClick={clearSearch}
               style={{
                 position: 'absolute',
                 right: '16px',
                 top: '50%',
                 transform: 'translateY(-50%)',
                 background: 'none',
                 border: 'none',
                 padding: '4px',
                 display: 'flex',
                 alignItems: 'center',
                 justifyContent: 'center',
                 borderRadius: '50%',
                 color: getColor('textLight'),
                 cursor: 'pointer',
                 transition: 'all 0.2s ease',
                 '&:hover': {
                   backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                   color: getColor('text'),
                 }
               }}
             >
               <X size={16} />
             </button>
           )}
         </div>
       </div>

       <div style={{
         display: 'flex',
         gap: '10px',
         borderBottom: `1px solid ${getColor('border')}`,
         paddingBottom: '14px',
         overflowX: 'auto',
         scrollbarWidth: 'none',
         msOverflowStyle: 'none',
         '&::-webkit-scrollbar': {
           display: 'none',
         },
       }}>
         <button 
           style={{
             display: 'flex',
             alignItems: 'center',
             gap: '10px',
             padding: '12px 20px',
             backgroundColor: activeTab === 'upload'
               ? isDarkMode ? 'rgba(99, 102, 241, 0.15)' : '#eef2ff'
               : 'transparent',
             border: 'none',
             borderRadius: '12px',
             color: activeTab === 'upload'
               ? getColor('primary')
               : getColor('textLight'),
             fontSize: '15px',
             fontWeight: activeTab === 'upload' ? '600' : '500',
             cursor: 'pointer',
             transition: 'all 0.2s ease',
             '&:hover': {
               backgroundColor: activeTab !== 'upload'
                 ? isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)'
                 : isDarkMode ? 'rgba(99, 102, 241, 0.2)' : '#e0e7ff',
             }
           }}
           onClick={() => setActiveTab('upload')}
         >
           <Upload size={18} />
           <span>Smart Import</span>
         </button>
         <button 
           style={{
             display: 'flex',
             alignItems: 'center',
             gap: '10px',
             padding: '12px 20px',
             backgroundColor: activeTab === 'assigned'
               ? isDarkMode ? 'rgba(6, 182, 212, 0.15)' : '#ecfeff'
               : 'transparent',
             border: 'none',
             borderRadius: '12px',
             color: activeTab === 'assigned'
               ? getColor('accent')
               : getColor('textLight'),
             fontSize: '15px',
             fontWeight: activeTab === 'assigned' ? '600' : '500',
             cursor: 'pointer',
             transition: 'all 0.2s ease',
             '&:hover': {
               backgroundColor: activeTab !== 'assigned'
                 ? isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)'
                 : isDarkMode ? 'rgba(6, 182, 212, 0.2)' : '#bae6fd',
             }
           }}
           onClick={() => setActiveTab('assigned')}
         >
           <Home size={18} />
           <span>Zimmerliste</span>
           <span style={{
             display: 'inline-flex',
             alignItems: 'center',
             justifyContent: 'center',
             minWidth: '24px',
             height: '24px',
             padding: '0 8px',
             borderRadius: '12px',
             backgroundColor: isDarkMode ? 'rgba(6, 182, 212, 0.2)' : '#a5f3fc',
             color: isDarkMode ? COLORS.dark.accent : COLORS.light.accent,
             fontSize: '13px',
             fontWeight: '600',
             opacity: animatedCount ? 0 : 1,
             animation: animatedCount ? 'counterFadeIn 1s forwards' : 'none',
             animationDelay: '0.2s',
           }}>
             {stats.assigned}
           </span>
         </button>
         <button 
           style={{
             display: 'flex',
             alignItems: 'center',
             gap: '10px',
             padding: '12px 20px',
             backgroundColor: activeTab === 'unassigned'
               ? isDarkMode ? 'rgba(236, 72, 153, 0.15)' : '#fce7f3'
               : 'transparent',
             border: 'none',
             borderRadius: '12px',
             color: activeTab === 'unassigned'
               ? getColor('secondary')
               : getColor('textLight'),
             fontSize: '15px',
             fontWeight: activeTab === 'unassigned' ? '600' : '500',
             cursor: 'pointer',
             transition: 'all 0.2s ease',
             '&:hover': {
               backgroundColor: activeTab !== 'unassigned'
                 ? isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)'
                 : isDarkMode ? 'rgba(236, 72, 153, 0.2)' : '#fbcfe8',
             }
           }}
           onClick={() => setActiveTab('unassigned')}
         >
           <User size={18} />
           <span>Ohne Zimmer</span>
           <span style={{
             display: 'inline-flex',
             alignItems: 'center',
             justifyContent: 'center',
             minWidth: '24px',
             height: '24px',
             padding: '0 8px',
             borderRadius: '12px',
             backgroundColor: isDarkMode ? 'rgba(236, 72, 153, 0.2)' : '#fbcfe8',
             color: isDarkMode ? COLORS.dark.secondary : COLORS.light.secondary,
             fontSize: '13px',
             fontWeight: '600',
             opacity: animatedCount ? 0 : 1,
             animation: animatedCount ? 'counterFadeIn 1s forwards' : 'none',
             animationDelay: '0.4s',
           }}>
             {stats.unassigned}
           </span>
         </button>
       </div>
     </div>

     {/* Stats Container */}
     <div style={{
       display: 'flex',
       gap: '16px',
       marginBottom: '32px',
       flexWrap: 'wrap',
     }}>
       <div 
         ref={el => statsRef.current[0] = el}
         style={{
           flex: '1 1 calc(33.333% - 16px)',
           minWidth: '220px',
           backgroundColor: isDarkMode ? 'rgba(6, 182, 212, 0.1)' : '#ecfeff',
           borderRadius: '16px',
           padding: '24px',
           display: 'flex',
           alignItems: 'center',
           gap: '20px',
           boxShadow: `0 4px 15px rgba(6, 182, 212, ${isDarkMode ? '0.15' : '0.07'})`,
           transition: 'transform 0.3s ease, box-shadow 0.3s ease',
           '&:hover': {
             transform: 'translateY(-4px)',
             boxShadow: `0 10px 20px rgba(6, 182, 212, ${isDarkMode ? '0.3' : '0.1'})`,
           },
         }}
       >
         <div style={{
           display: 'flex',
           alignItems: 'center',
           justifyContent: 'center',
           width: '56px',
           height: '56px',
           borderRadius: '16px',
           backgroundColor: isDarkMode ? 'rgba(6, 182, 212, 0.2)' : '#a5f3fc',
           color: getColor('accent'),
         }}>
           <Home size={28} />
         </div>
         <div>
           <div className={animatedCount ? 'animate-counter' : ''} style={{
             fontSize: '32px',
             fontWeight: '700',
             color: getColor('accent'),
             lineHeight: '1.2',
           }}>
             {stats.assigned}
           </div>
           <div style={{
             fontSize: '15px',
             color: isDarkMode ? COLORS.dark.textMedium : COLORS.light.textMedium,
           }}>
             Zugewiesene Zimmer
           </div>
         </div>
       </div>
       <div 
         ref={el => statsRef.current[1] = el}
         style={{
           flex: '1 1 calc(33.333% - 16px)',
           minWidth: '220px',
           backgroundColor: isDarkMode ? 'rgba(99, 102, 241, 0.1)' : '#eef2ff',
           borderRadius: '16px',
           padding: '24px',
           display: 'flex',
           alignItems: 'center',
           gap: '20px',
           boxShadow: `0 4px 15px rgba(99, 102, 241, ${isDarkMode ? '0.15' : '0.07'})`,
           transition: 'transform 0.3s ease, box-shadow 0.3s ease',
           '&:hover': {
             transform: 'translateY(-4px)',
             boxShadow: `0 10px 20px rgba(99, 102, 241, ${isDarkMode ? '0.3' : '0.1'})`,
           },
         }}
       >
         <div style={{
           display: 'flex',
           alignItems: 'center',
           justifyContent: 'center',
           width: '56px',
           height: '56px',
           borderRadius: '16px',
           backgroundColor: isDarkMode ? 'rgba(99, 102, 241, 0.2)' : '#c7d2fe',
           color: getColor('primary'),
         }}>
           <Users size={28} />
         </div>
         <div>
           <div className={animatedCount ? 'animate-counter' : ''} style={{
             fontSize: '32px',
             fontWeight: '700',
             color: getColor('primary'),
             lineHeight: '1.2',
           }}>
             {stats.total}
           </div>
           <div style={{
             fontSize: '15px',
             color: isDarkMode ? COLORS.dark.textMedium : COLORS.light.textMedium,
           }}>
             Gesamt Lernende
           </div>
         </div>
       </div>
       <div 
         ref={el => statsRef.current[2] = el}
         style={{
           flex: '1 1 calc(33.333% - 16px)',
           minWidth: '220px',
           backgroundColor: isDarkMode ? 'rgba(236, 72, 153, 0.1)' : '#fce7f3',
           borderRadius: '16px',
           padding: '24px',
           display: 'flex',
           alignItems: 'center',
           gap: '20px',
           boxShadow: `0 4px 15px rgba(236, 72, 153, ${isDarkMode ? '0.15' : '0.07'})`,
           transition: 'transform 0.3s ease, box-shadow 0.3s ease',
           '&:hover': {
             transform: 'translateY(-4px)',
             boxShadow: `0 10px 20px rgba(236, 72, 153, ${isDarkMode ? '0.3' : '0.1'})`,
           },
         }}
       >
         <div style={{
           display: 'flex',
           alignItems: 'center',
           justifyContent: 'center',
           width: '56px',
           height: '56px',
           borderRadius: '16px',
           backgroundColor: isDarkMode ? 'rgba(236, 72, 153, 0.2)' : '#fbcfe8',
           color: getColor('secondary'),
         }}>
           <User size={28} />
         </div>
         <div>
           <div className={animatedCount ? 'animate-counter' : ''} style={{
             fontSize: '32px',
             fontWeight: '700',
             color: getColor('secondary'),
             lineHeight: '1.2',
           }}>
             {stats.unassigned}
           </div>
           <div style={{
             fontSize: '15px',
             color: isDarkMode ? COLORS.dark.textMedium : COLORS.light.textMedium,
           }}>
             Ohne Zimmer
           </div>
         </div>
       </div>
     </div>

     {/* Main Content Based on Active Tab */}
     <div>
       {activeTab === 'upload' && renderUploadSection()}
       {activeTab === 'assigned' && renderAssignmentsSection()}
       {activeTab === 'unassigned' && renderUnassignedUsersSection()}
     </div>

     {/* Assignment Form Modal */}
     {renderAssignmentForm()}

     {/* CSS Animations */}
     <style>{`
       @keyframes fadeIn {
         from { opacity: 0; }
         to { opacity: 1; }
       }
       
       @keyframes fadeInUp {
         from {
           opacity: 0;
           transform: translateY(20px);
         }
         to {
           opacity: 1;
           transform: translateY(0);
         }
       }
       
       @keyframes slideInUp {
         from {
           opacity: 0;
           transform: translateY(30px);
         }
         to {
           opacity: 1;
           transform: translateY(0);
         }
       }
       
       @keyframes slideInDown {
         from {
           opacity: 0;
           transform: translateY(-20px);
         }
         to {
           opacity: 1;
           transform: translateY(0);
         }
       }
       
       @keyframes spin {
         from { transform: rotate(0deg); }
         to { transform: rotate(360deg); }
       }
       
       @keyframes pulse {
         0% { transform: scale(0.95); opacity: 0.7; }
         50% { transform: scale(1.05); opacity: 1; }
         100% { transform: scale(0.95); opacity: 0.7; }
       }
       
       @keyframes shimmer {
         from { transform: translateX(-100%); }
         to { transform: translateX(100%); }
       }
       
       @keyframes counterFadeIn {
         0% { opacity: 0; transform: scale(0.8); }
         70% { opacity: 1; transform: scale(1.1); }
         100% { opacity: 1; transform: scale(1); }
       }
       
       .notification-banner {
         animation: slideInDown 0.4s cubic-bezier(0.16, 1, 0.3, 1);
       }
       
       .modal-content {
         animation: slideInUp 0.4s cubic-bezier(0.16, 1, 0.3, 1);
       }
       
       .spinner {
         animation: spin 1s linear infinite;
       }
       
       .pulse-loader {
         animation: pulse 1.5s ease-in-out infinite;
       }
       
       .progress-shimmer {
         animation: shimmer 1.5s infinite;
       }
       
       .animate-counter {
         animation: counterFadeIn 1s forwards;
       }
       
       .typing-dots .dots {
         display: inline-block;
         animation: typingDots 1.4s infinite;
       }
       
       @keyframes typingDots {
         0%, 20% { content: '.'; }
         40% { content: '..'; }
         60%, 100% { content: '...'; }
       }
       
       .file-drop-zone label {
         transition: all 0.3s ease;
       }
       
       .file-drop-zone.drag-over label {
         transform: scale(0.98);
         background-color: ${isDarkMode ? 'rgba(99, 102, 241, 0.15)' : '#eef2ff'};
         border-color: ${getColor('primary')};
         box-shadow: 0 0 0 3px ${isDarkMode ? 'rgba(99, 102, 241, 0.2)' : 'rgba(99, 102, 241, 0.1)'};
       }
       
       /* Card hover effects */
       .user-card {
         transition: all 0.3s ease;
       }
       
       .user-card:hover {
         transform: translateY(-4px);
         box-shadow: 0 10px 20px rgba(0, 0, 0, ${isDarkMode ? '0.25' : '0.1'});
       }
       
       .user-card:hover .user-assign-button {
         background-color: ${isDarkMode ? 'rgba(99, 102, 241, 0.25)' : '#c7d2fe'};
         transform: translateY(-2px);
       }
       
       .room-card {
         transition: all 0.3s ease;
       }
       
       .room-card:hover {
         transform: translateY(-4px);
         box-shadow: 0 10px 20px rgba(0, 0, 0, ${isDarkMode ? '0.25' : '0.1'});
       }
     `}</style>
   </div>
 );
};

export default PremiumRoomManagement; 